import { create } from 'zustand';
import {
  fetchConversationsListParams,
  SidebarItem,
} from '../../../components/blocks/layout/types/sidebar-conversation';
import {
  Attachment,
  FileMetadata,
  initialFeaturesState,
  Message,
  MessageDeliveryStatus,
  MessageGeneration,
  Mention,
  RelativeSearchQuestion,
  ReplyMessageData,
  SearchSource,
  Source,
  Tools,
} from '../../../features/chat/types/chat';
import { v4 as uuidv4 } from 'uuid';
import { clients } from '../../../lib/https';
import { useModelStore } from './use-model-store';
import { AssistantPositionRemainingType, ResponseTimeAnalytics, SSEResponseTypes } from '../../../models/websockets.types';
import { WebsocketServices } from '../../../services/websocket-services';
import { ButtonIds } from 'features/chat/types/chat';
import { ConfigurationSettings } from '../../../models/configuration-settings';
import { getSearchRelatedQuestionList, getSSEStream, getGroupChatStream, getTitle2, stopGeneration } from '../../../services/sse-service';
import { getFile } from '../../../services/storage-service';

import { getUpdatedMessages, parseThinkingContent } from 'utils/chat-utilities';
import { Project, ProjectItem } from 'features/projects/types/create-project';
import { trackEvent } from 'analytics/tracker';
import { EVENTS, FeatureName } from 'analytics/events';
import { MessageRole } from 'features/chat/types/chat';
import { useCommonStore } from '../common/useCommonStore';
import { ApiConversationResponse, fetchProjectConversations } from 'features/projects/services/projects-services';
import { getUsers, IamData } from 'features/iam/services/user-service';
import { getConversationById, ConversationEvent } from 'services/conversation-services';
import { getGroupChatParticipants } from 'features/group-chat/services/group-services';
import { useCollaborationSocketStore } from '../collaboration/use-collaboration-store';
import { DEFAULT_CONVERSATION_NAME } from 'utils/constants/chat-constants';
import { isToday, isYesterday } from 'date-fns';

const extractLastResponseContent = (content: string) => {
  const lastIndefOfResponseOpenTag = content.lastIndexOf('<response>');
  if (lastIndefOfResponseOpenTag !== -1) {
    const lastIndefOfResponseCloseTag = content.indexOf('</response>', lastIndefOfResponseOpenTag);
    if (lastIndefOfResponseCloseTag !== -1) {
      return content.slice(
        lastIndefOfResponseOpenTag + '<response>'.length,
        lastIndefOfResponseCloseTag
      );
    } else {
      return content.slice(lastIndefOfResponseOpenTag + '<response>'.length);
    }
  }
  return content;
};

/**
 * Formats a date to show "Today at HH:mm AM/PM" or "Yesterday at HH:mm AM/PM" or date format
 */
const formatDateWithTime = (date: Date): string => {
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  const minutesStr = minutes.toString().padStart(2, '0');
  const timeStr = `${displayHours}:${minutesStr} ${ampm}`;

  if (isToday(date)) {
    return `Today at ${timeStr}`;
  }
  if (isYesterday(date)) {
    return `Yesterday at ${timeStr}`;
  }
  // For older dates, show the date
  const day = date.getDate();
  const month = date.toLocaleString('default', { month: 'short' });
  const year = date.getFullYear();
  return `${month} ${day}, ${year} at ${timeStr}`;
};

function posthogSSEEvent(responseType: SSEResponseTypes, additionalInfo: any) {
  const isError = [
    SSEResponseTypes.SERVER_ERROR,
    SSEResponseTypes.FILE_GENERATION_FAILED,
    SSEResponseTypes.IMAGE_GENERATION_FAILED,
    SSEResponseTypes.CONTEXT_WINDOW_EXCEEDED,
    SSEResponseTypes.IMAGE_GENERATION_LIMIT_EXCEEDED
  ].includes(responseType);

  const isRegularAnswer = [
    SSEResponseTypes.RESPONSE_SOURCES,
    SSEResponseTypes.SEARCH_SOURCES,
    SSEResponseTypes.TOKEN_ANALYTICS,
    SSEResponseTypes.CONVERSATION_TITLE,
    SSEResponseTypes.END_OF_STREAM,
    SSEResponseTypes.LLM_THINKING,
    SSEResponseTypes.LLM_ANSWER,
    SSEResponseTypes.SEARCH_RESPONSE
  ].includes(responseType);

  if (isError) {
    trackEvent(EVENTS.SSE_ERROR_RESPONSE(responseType, additionalInfo));
  } else if (!isRegularAnswer) {
    trackEvent(EVENTS.SSE_INFO_RESPONSE(responseType, additionalInfo));
  }
}


const extractFeedbackFromReactions = (
  reactions: any,
  currentUserId: string | undefined
): boolean | undefined => {
  if (!reactions || !currentUserId) {
    return undefined;
  }

  // Check if user has liked the message
  if (reactions.LIKE && Array.isArray(reactions.LIKE.userIds)) {
    if (reactions.LIKE.userIds.includes(currentUserId)) {
      return true;
    }
  }

  // Check if user has disliked the message
  if (reactions.DISLIKE && Array.isArray(reactions.DISLIKE.userIds)) {
    if (reactions.DISLIKE.userIds.includes(currentUserId)) {
      return false;
    }
  }

  return undefined;
};

export interface StreamingQueueItem {
  position: number;
  conversationId: string;
  messageID: string;
  type: string;
  userID: string[] | null;
  parentIds: string[];
  streamingResponse: any[];
}

export interface MessageQueueItem {
  position: number;
  key: string; // dummyUserMessageId
  content: string; // messageBody
  attachmentsMetadata?: FileMetadata[];
  parentIds?: string[];
  mentions?: string[];
  isRegenerate?: boolean;
  imageGeneration?: boolean;
  integrationSources?: string[];
  searchOnline?: boolean;
}

export interface ConversationState {
  id: string;
  title: string;
  messages: Message[];
  isLoading: boolean;
  isCurrentMessageLoading: boolean;
  isCurrentResponseGenerating: boolean;
  isAnalyzing: boolean;
  isAnalyzed: boolean;
  hasFetchedInitialMessages: boolean;
  error?: string | null;
  modelName?: string;
  allFileIds?: string[];
  streamingAssistantMessageId?: string | null;
  streamingAssistantMessageIds?: string[]; // Array to track multiple concurrent streaming messages (for group chats)
  toolsState: Record<ButtonIds, boolean>;
  isGroupChat?: boolean;
  participants?: IamData[];
  removedParticipants?: IamData[];
  isRecentlyAdded?: boolean;
  assistantResponseRemaining?: number;
  activeStreamsMap?: Map<string, boolean>;
  isCancelOnGoing?: boolean;
  customGptId?: string;
}

export interface ChatStoreState {
  itemIdFilePathMap: Map<string, string>;
  activeConversationId: string | null;
  activeAudioMessageId: string | null;
  conversations: Record<string, ConversationState>;
  sidebarConversationsList: SidebarItem[];
  searchConversationsList: SidebarItem[];
  totalConversationsCount: number;
  totalIndividualConversationsCount: number;
  totalGroupConversationsCount: number;
  sidebarProjectsList: Project[];
  updateProjectConversation: (conversationId: string, configurationSettings: ConfigurationSettings) => Promise<void>;
  setSidebarProjectsList: (projects: Project[]) => void;
  updateProjectItems: (projectId: string, conversations: ProjectItem[]) => void;
  isSidebarLoading: boolean;
  sidebarError: string | null;
  isNewChat: boolean;
  hasParticipant: boolean;
  setHasParticipant: (value: boolean) => void;
  hasParticipantsArray: IamData[];
  setHasParticipantsArray: (value: IamData[]) => void;
  isLoadingParticipants: boolean;
  setIsLoadingParticipants: (value: boolean) => void;
  hasAddedAssistantAsParticipant: boolean;
  setHasAddedAssistantAsParticipant: (value: boolean) => void;
  isGroupChat: boolean;
  setGroupChat: (value: boolean) => void;
  conversationTitleRefetched: boolean;
  setConversationTitleRefetched: (value: boolean) => void;
  replyMessage: ReplyMessageData | null;
  setReplyMessage: (value: ReplyMessageData | null) => void;
  commentMessage: Message | null;
  setCommentMessage: (value: Message | null) => void;
  _handleWebSocketMessage: (parsedResponse: any, config: ConfigurationSettings) => void;
  _addMessageToActiveConversation: (msg: Message, recordIdToUpdate?: string) => void;
  handleStreamingQueue: (streamingQueue: StreamingQueueItem[], config: ConfigurationSettings) => void;
  headerTitle: string;
  setHeaderTitle: (value: string) => void;
  hasGroupChatParticipantsArray: { [key: string]: IamData[] };

  setHasGroupChatParticipantsArray: (conversationId: string, participants: IamData[]) => void;
  fetchAndSetGroupChatParticipants: (conversationId: string, config: ConfigurationSettings, refetch?: boolean, loading?: boolean) => Promise<void>;
  isIncognito: boolean;
  setIsIncognitoChat: (value: boolean) => void;
  sendMessage: (
    content: string,
    config: ConfigurationSettings,
    newConversationId?: string,
    attachments?: Attachment[],
    isFeatureSelected?: Record<ButtonIds, boolean>,
    addToSidebar?: boolean,
    parentIds?: string[],
    sources?: string[],
    isRegenerate?: boolean,
    originalRegenerationMessageId?: string,
    mentions?: Mention[],
    parentUserGenerationId?: string,
    isIncognitoChat?: boolean,
    customGptId?: string,
  ) => Promise<void>;

  sendMessageToProject: (
    content: string,
    config: ConfigurationSettings,
    projectId: string,
    newConversationId?: string,
    attachments?: Attachment[],
    isFeatureSelected?: Record<ButtonIds, boolean>,

  ) => Promise<void>;

  fetchAttachmentMetadata: (attachmentIds: string[], sender: MessageRole, config: ConfigurationSettings) => Promise<FileMetadata[]>;
  sendSSEMessage: (messageBody: string, config: ConfigurationSettings, conversationId: string) => void;
  sendGroupChatStream: (
    messageBody: string,
    config: ConfigurationSettings,
    conversationId: string,
    attachmentsMetadata?: FileMetadata[],
    replyMessage?: ReplyMessageData | null,
    mentions?: string[],
    isRegenerate?: boolean,
    originalRegenerationMessageId?: string,
    imageGeneration?: boolean,
    integrationSources?: string[],
    searchOnline?: boolean,
    retryMessageId?: string, // ID of message to retry (prevents creating new message)
    dummyUserMessageId?: string, // Dummy ID generated for the user message that needs to be replaced
  ) => Promise<void>;
  retryFailedMessage: (
    messageId: string,
    config: ConfigurationSettings,
    conversationId: string,
  ) => Promise<void>;
  regenerateResponse: (recordId: string, config: ConfigurationSettings) => Promise<void>;
  setActiveConversationId: (conversationId: string | null) => void;
  setActiveConversationModelName: (modelName: string) => void;
  setActiveAudioMessageId: (messageId: string | null) => void;
  setActiveConversation: (
    conversationId: string | null,
    config: ConfigurationSettings,
    participants?: IamData[]
  ) => Promise<void>;
  fetchAndSetConversationMessages: (
    conversationId: string,
    config: ConfigurationSettings,
    participants?: IamData[]
  ) => Promise<void>;
  invitedUsersData: IamData[];
  setInvitedUsersData: (users: IamData[]) => void;
  generateConversationTitle: (conversationId: string, config: ConfigurationSettings) => void;
  updateConversationTitleInStore: (conversationId: string, newTitle: string) => void;
  updateProjectNameInStore: (projectId: string, newName: string) => void;
  fetchAndUpdateProjectAllConversations: (configurationSettings: ConfigurationSettings, projectId: string) => Promise<void>;
  appendToSidebarConversationsList: (newItem: SidebarItem) => void;
  appendProjectItemToSidebarProjectsList: (newItem: ProjectItem, projectId: string) => void;
  appendProjectToSidebarProjectsList: (newItem: Project) => void;
  _sortSidebarConversations: (newList: SidebarItem[]) => SidebarItem[];
  _moveConversationToToday: (conversationId: string) => void;
  fetchSidebarConversations: (
    config: ConfigurationSettings,
    params?: fetchConversationsListParams
  ) => Promise<void>;
  removeConversationFromSidebarById: (conversationId: string) => void;
  removeConversationFromSidebarProjectListById: (conversationId: string) => void;
  fetchSearchResults: (search: string, config: ConfigurationSettings) => Promise<void>;
  setItemIdFilePathMap: (key: string, value: string) => void;
  clearItemIdFilePathMap: () => void;
  changeActiveGenerationIndex: (recordId: string, index: number) => void;
  updateMessageFeedback: (
    recordId: string,
    messageId: string,
    feedback: boolean | undefined
  ) => void;
  setNewChat: (isNew: boolean) => void;
  disableNewChat: boolean;
  setDisableNewChat: (disable: boolean) => void;
  setNewChatWithModel: (isNew: boolean) => void;
  reset: () => void;
  resetErrorByConversationId: (conversationId: string) => void;
  getSearchRelatedList: (data: { conversationId: string; messageId: string; type: string; modelName: string; }, config: ConfigurationSettings) => void;
  getSearchRelatedImageList: (data: { conversationId: string; messageId: string; type: string; modelName: string; }, config: ConfigurationSettings) => void;
  getSearchRelatedVideoList: (data: { conversationId: string; messageId: string; type: string; modelName: string; }, config: ConfigurationSettings) => void;
  setrelatedSearcheQuestionsList: (conversationId: string, relatedSearchedQuestionsList: RelativeSearchQuestion[], messageId?: string) => void;
  setrelatedSearcheImageList: (conversationId: string, relatedSearchedImageList: RelativeSearchQuestion[], messageId?: string) => void;
  setrelatedSearcheVideoList: (conversationId: string, relatedSearchedVideoList: RelativeSearchQuestion[], messageId?: string) => void;
  handleGroupChatNotification: (notification: any, config?: ConfigurationSettings, type?: string, userIds?: { invitedBy: string, invitedTo: string[] }) => Promise<void>;
  handleStreamingMessageBatch: (messages: any[], config: ConfigurationSettings) => void;
  temporaryConversation: (
    content: string,
    attachments?: Attachment[],
    isFeatureSelected?: Record<ButtonIds, boolean>
  ) => { temporaryConversationId: string; recordId: string; assistantMessageId: string };
  replaceConversationId: (oldConversationId: string, newConversationId: string, isIncognitoChat: boolean) => void;
  fetchUsersData: (userIds: string[], config: ConfigurationSettings) => Promise<{ data: IamData[]; totalCount: number }>;
  messageQueue: MessageQueueItem[];
  addToMessageQueue: (item: MessageQueueItem) => void;
  removeFromMessageQueue: (key: string) => void;
  clearMessageQueue: () => void;
  isProcessingMessageQueue: boolean;
  setIsProcessingMessageQueue: (value: boolean) => void;
  processNextMessageFromQueue: (conversationId: string, config: ConfigurationSettings) => Promise<void>;
  updateMessageAnalytics: (recordId: string, messageId: string, analytics: ResponseTimeAnalytics | undefined) => void;
  stopGeneration: (conversationId: string, config: ConfigurationSettings) => void;
  shareTokens: Record<string, string>;
  setConversationShareToken: (conversationId: string, token: string) => void;
  getConversationShareToken: (conversationId: string) => string | undefined;
}

export const initialConversationState = (id: string, title = DEFAULT_CONVERSATION_NAME, participants?: IamData[]): ConversationState => {
  // Ensure modelName is always set - use selectedModel or fallback to 'auto'



  return {
    id,
    title,
    messages: [],
    isLoading: false,
    isCurrentMessageLoading: false,
    isCurrentResponseGenerating: false,
    isAnalyzing: false,
    isAnalyzed: false,
    hasFetchedInitialMessages: false,
    error: null,
    modelName: "auto",
    allFileIds: [],
    streamingAssistantMessageId: null,
    streamingAssistantMessageIds: [], // Track multiple concurrent streaming messages
    toolsState: initialFeaturesState,
    isGroupChat: false,
    participants,
    removedParticipants: [],
    isRecentlyAdded: false,
    assistantResponseRemaining: 0,
    activeStreamsMap: new Map(),
    isCancelOnGoing: false,
    customGptId: undefined
  };
};

export const useChatStore = create<ChatStoreState>((set, get) => ({
  hasGroupChatParticipantsArray: {},
  messageQueue: [],
  isProcessingMessageQueue: false,
  isIncognito: false,
  setIsIncognitoChat: (value: boolean) => {
    set({ isIncognito: value });
  },
  shareTokens: {},
  setConversationShareToken: (conversationId: string, token: string) => {
    set((state) => ({
      shareTokens: {
        ...state.shareTokens,
        [conversationId]: token,
      },
    }));
  },
  getConversationShareToken: (conversationId: string) => {
    return get().shareTokens[conversationId];
  },
  setIsProcessingMessageQueue: (value: boolean) => {
    set({ isProcessingMessageQueue: value });
  },
  addToMessageQueue: (item: MessageQueueItem) => {
    set((state) => ({
      messageQueue: [...state.messageQueue, item],
    }));
  },
  removeFromMessageQueue: (key: string) => {
    set((state) => {
      const updatedQueue = state.messageQueue.filter((item) => item.key !== key);
      return { messageQueue: updatedQueue };
    });
  },
  clearMessageQueue: () => {
    set({ messageQueue: [] });
  },
  processNextMessageFromQueue: async (conversationId: string, config: ConfigurationSettings) => {
    const state = get();

    // Don't process if already processing or queue is empty
    if (state.isProcessingMessageQueue || state.messageQueue.length === 0) {
      return;
    }

    // Get the first message from queue (FIFO)
    const nextMessage = state.messageQueue[0];
    if (!nextMessage) {
      return;
    }

    // Mark as processing
    get().setIsProcessingMessageQueue(true);

    try {
      // Extract message data
      const {
        key: dummyUserMessageId,
        content: messageBody,
        attachmentsMetadata,
        parentIds = [],
        mentions,
        isRegenerate = false,
        imageGeneration = false,
        integrationSources,
        searchOnline = false,
      } = nextMessage;

      // Make HTTP call
      const response: any = await getGroupChatStream(
        messageBody,
        config,
        conversationId,
        attachmentsMetadata,
        parentIds,
        mentions,
        isRegenerate,
        imageGeneration,
        integrationSources,
        searchOnline
      );

      // console.log('response in processNextMessageFromQueue', response);

      if (response?.id) {
        set((state) => {
          const current = state.conversations[conversationId];
          const newId = response?.id;

          if (!current || !newId || !dummyUserMessageId) return state;

          const messages = current.messages;

          // Check if dummy exists anywhere
          const dummyFound = messages.some(
            (m) =>
              m.recordId === dummyUserMessageId ||
              m.userRecordId === dummyUserMessageId ||
              m.generations?.some((g) => g.id === dummyUserMessageId)
          );

          if (!dummyFound) return state;

          const finalMessages = messages.map((msg) => {
            const isDummyUserMsg = msg.recordId === dummyUserMessageId;
            const isDummyAssistantLink =
              msg.role === MessageRole.ASSISTANT &&
              msg.userRecordId === dummyUserMessageId;

            // Update dummy USER message → replace ID
            if (isDummyUserMsg) {
              return {
                ...msg,
                recordId: newId,
                deliveryStatus: MessageDeliveryStatus.DELIVERED,
                generations: msg.generations?.map((g) =>
                  g.id === dummyUserMessageId ? { ...g, id: newId } : g
                ),
              };
            }

            if (isDummyAssistantLink) {
              return {
                ...msg,
                userRecordId: newId,

              };
            }

            return msg;
          });

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...current,
                messages: finalMessages,
              },
            },
          };
        });
      }

      // Remove message from queue after response
      // get().removeFromMessageQueue(dummyUserMessageId);

      // Mark as not processing
      // get().setIsProcessingMessageQueue(false);

      // // Process next message from queue
      // get().processNextMessageFromQueue(conversationId, config);

    } catch (error) {
      // Mark as not processing on error
      get().setIsProcessingMessageQueue(false);

      // Remove message from queue on error
      get().removeFromMessageQueue(nextMessage.key);

      // Try to process next message
      get().processNextMessageFromQueue(conversationId, config);

      throw error;
    }
  },
  setHasGroupChatParticipantsArray: (conversationId: string, value: IamData[]) => {
    set((state) => ({
      hasGroupChatParticipantsArray: {
        ...state.hasGroupChatParticipantsArray,
        [conversationId]: value,
      },
      hasParticipant: value.length > 0 ? true : false,
      hasAddedAssistantAsParticipant: value.length === 0 ? true : false,
      isLoadingParticipants: false,
      hasParticipantsArray: value,
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...(state.conversations[conversationId] || initialConversationState(conversationId)),
          participants: value,
          // Ensure modelName is always set
          modelName: state.conversations[conversationId]?.modelName || useModelStore.getState().selectedModel?.id || 'auto',
        },
      },
    }));
  },
  activeConversationId: null,
  activeAudioMessageId: null,
  conversations: {},
  invitedUsersData: [],
  setInvitedUsersData: (users) => set({ invitedUsersData: users }),
  sidebarConversationsList: [],
  searchConversationsList: [],
  totalConversationsCount: 0,
  totalIndividualConversationsCount: 0,
  totalGroupConversationsCount: 0,
  sidebarProjectsList: [],
  setSidebarProjectsList: (projects) => set({ sidebarProjectsList: projects }),
  updateProjectItems: (projectId, conversations) => set((state) => ({
    sidebarProjectsList: state.sidebarProjectsList.map((project) =>
      project.id === projectId ? { ...project, items: conversations } : project
    ),
  })),
  isSidebarLoading: false,
  sidebarError: null,
  isNewChat: false,
  hasParticipant: false,
  hasParticipantsArray: [],
  isLoadingParticipants: false,
  hasAddedAssistantAsParticipant: false,
  setHasAddedAssistantAsParticipant: (value: boolean) => set({ hasAddedAssistantAsParticipant: value }),
  isGroupChat: false,
  setGroupChat: (value: boolean) => set({ isGroupChat: value }),
  conversationTitleRefetched: false,
  setConversationTitleRefetched: (value: boolean) => set({ conversationTitleRefetched: value }),
  replyMessage: null,
  setReplyMessage: (value: ReplyMessageData | null) => set({ replyMessage: value }),
  commentMessage: null,
  setCommentMessage: (value: Message | null) => set({ commentMessage: value }),
  itemIdFilePathMap: new Map<string, string>(),
  headerTitle: DEFAULT_CONVERSATION_NAME,
  setHeaderTitle: (value: string) => set({ headerTitle: value }),


  fetchAttachmentMetadata: async (attachmentIds: string[], sender: MessageRole, config: ConfigurationSettings): Promise<FileMetadata[]> => {
    if (!attachmentIds || attachmentIds.length === 0) return [];

    const fileMetadata: FileMetadata[] = [];

    for (const fileId of attachmentIds) {
      try {
        const response = await getFile(fileId, config || {} as ConfigurationSettings);
        if (response.isSuccess) {
          // Extract filename and extension from the response
          const fileName = response.name || `file_${fileId}`;
          const extension = fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() || 'unknown' : 'unknown';

          // Use itemId from response as fileId, fallback to provided fileId
          const responseFileId = response.itemId || fileId;
          const createdAt = response.createDate || new Date().toISOString();
          const updatedAt = response.createDate || new Date().toISOString(); // Use createDate if updatedAt not available
          const size = response.sizeInBytes || 0;

          fileMetadata.push({
            fileId: responseFileId,
            fileName: fileName,
            extension: extension,
            createdAt: createdAt,
            updatedAt: updatedAt,
            size: size,
            sender: sender,
          });
        } else {

          fileMetadata.push({
            fileId: fileId,
            fileName: `file_${fileId}`,
            extension: 'unknown',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            size: 0,
            sender: sender,
          });
        }
      } catch (error) {

        fileMetadata.push({
          fileId: fileId,
          fileName: `file_${fileId}`,
          extension: 'unknown',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          size: 0,
          sender: sender,
        });
      }
    }

    return fileMetadata;
  },
  sendSSEMessage: async (messageBody: string, config: ConfigurationSettings, conversationId: string) => {
    let response;
    try {
      response = await getSSEStream(messageBody, config);
    } catch (error) {
      trackEvent(EVENTS.ERROR(error, {
        endpoint: config.paths.taskforce.chatStream
      }));
      set((state) => ({
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...state.conversations[conversationId],
            isCurrentResponseGenerating: false,
            isCurrentMessageLoading: false,
            isAnalyzing: false,
            error: "SOMETHING_WENT_WRONG_ERROR_MESSAGE"
          },
        },
      }));
      return;
    }

    const reader = response?.body?.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = "";

    while (reader) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split("\n\n");
      buffer = parts.pop() ?? "";

      for (const part of parts) {
        const lines = part.split('\n');
        for (let line of lines) {
          line = line.trim();
          if (line.startsWith('data: ')) {
            const payload = line.replace('data: ', '').trim();
            if (payload === '[DONE]') return;

            try {
              const parsedResponse = JSON.parse(payload);
              console.log('parsedResponse in sendSSEMessage', parsedResponse);
              get()._handleWebSocketMessage(parsedResponse, config);
            } catch (error) {
              console.error('Partial or invalid JSON, waiting for next chunk:', payload);
            }
          }
        }
      }
    }
  },

  sendGroupChatStream: async (
    messageBody: string,
    config: ConfigurationSettings,
    conversationId: string,
    attachmentsMetadata?: FileMetadata[],
    replyMessage?: ReplyMessageData | null,
    mentions?: string[],
    isRegenerate?: boolean,
    originalRegenerationMessageId?: string,
    imageGeneration?: boolean,
    integrationSources?: string[],
    searchOnline?: boolean,
    retryMessageId?: string, // ID of message to retry (prevents creating new message)
    dummyUserMessageId?: string, // Dummy ID generated for the user message that needs to be replaced
  ) => {

    let parentIds = replyMessage?.generation ? [replyMessage?.generation.id] : [];
    if (isRegenerate && originalRegenerationMessageId) {
      parentIds = [originalRegenerationMessageId ?? ''];
    }
    let response: any;
    try {
      response = await getGroupChatStream(
        messageBody,
        config,
        conversationId,
        attachmentsMetadata,
        parentIds,
        mentions,
        isRegenerate,
        imageGeneration,
        integrationSources,
        searchOnline
      );
      if (response?.id) {
        set((state) => {
          const current = state.conversations[conversationId];
          const newId = response?.id;

          if (!current || !newId || !dummyUserMessageId) return state;

          const messages = current.messages;

          // Check if dummy exists anywhere
          const dummyFound = messages.some(
            (m) =>
              m.recordId === dummyUserMessageId ||
              m.userRecordId === dummyUserMessageId
          );

          if (!dummyFound) return state;

          const finalMessages = messages.map((msg) => {
            const isDummyUserMsg = msg.recordId === dummyUserMessageId;
            const isDummyAssistantLink =
              msg.role === MessageRole.ASSISTANT &&
              msg.userRecordId === dummyUserMessageId;

            // Update dummy USER message → replace ID
            if (isDummyUserMsg) {
              return {
                ...msg,
                recordId: newId,
                deliveryStatus: MessageDeliveryStatus.DELIVERED,
                generations: msg.generations?.map((g) =>
                  g.id === dummyUserMessageId ? { ...g, id: newId } : g
                ),
              };
            }

            if (isDummyAssistantLink) {
              return {
                ...msg,
                userRecordId: newId,
              };
            }

            return msg;
          });

          // console.log('finalMessages', finalMessages)

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...current,
                messages: finalMessages,
              },
            },
          };
        });
      }


    } catch (error) {

      trackEvent(EVENTS.ERROR(error, {
        endpoint: config.paths.taskforce.collaboration
      }));

      // Mark the last user message as failed for group chats
      set((state) => {
        const conv = state.conversations[conversationId];
        if (!conv) return state;
        if (!conv || !conv.messages) return {
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...conv,
              isCurrentResponseGenerating: false,
              isCurrentMessageLoading: false,
              error: "SOMETHING_WENT_WRONG_ERROR_MESSAGE"
            },
          },
        };
        const messages = [...conv.messages];


        // Mark the message as failed - use retryMessageId if provided, otherwise find last PENDING message
        const updatedMessages = messages.map((msg) => {
          // If retryMessageId is provided, update that specific message
          if (retryMessageId && msg.recordId === retryMessageId) {
            return {
              ...msg,
              deliveryStatus: MessageDeliveryStatus.FAILED,
            };
          }
          // Otherwise, find the last USER_CHAT message with PENDING status
          if (!retryMessageId && msg.role === MessageRole.USER_CHAT &&
            (msg.deliveryStatus === MessageDeliveryStatus.PENDING || !msg.deliveryStatus)) {
            // Check if this is the last PENDING message
            const userChatMessages = messages.filter(m => m.role === MessageRole.USER_CHAT);
            const lastPendingMessage = userChatMessages[userChatMessages.length - 1];
            if (msg.recordId === lastPendingMessage?.recordId) {
              return {
                ...msg,
                deliveryStatus: MessageDeliveryStatus.FAILED,
              };
            }
          }
          return msg;
        });

        return {
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...conv,
              messages: updatedMessages,
              isCurrentResponseGenerating: false,
              isCurrentMessageLoading: false,
              error: "SOMETHING_WENT_WRONG_ERROR_MESSAGE"
            },
          },
        };
      });
      return;
    }

  },

  sendMessageToProject: async (
    content: string,
    config: ConfigurationSettings,
    projectId: string,
    newConversationId?: string,
    attachments?: Attachment[],
    isFeatureSelected?: Record<ButtonIds, boolean>,
    isGroupChatParticipants?: boolean,
    sources?: string[],
  ) => {
    // console.log('isGroupChatParticipants in sendMessageToProject', isGroupChatParticipants);
    const { activeConversationId, replyMessage } = get();
    const selectedModel = useModelStore.getState().selectedModel;
    const newFileIds = attachments?.map((item) => item.fileId) ?? [];
    let updatedAllFileIds = newFileIds;

    // Extract parentMessage data from replyMessage
    const parentMessage = replyMessage?.generation && replyMessage?.repliedUser ? {
      senderName: replyMessage.repliedUser.firstName + (replyMessage.repliedUser.lastName ? ` ${replyMessage.repliedUser.lastName}` : ''),
      content: replyMessage.generation.content,
      time: replyMessage.generation.createdDate || Date.now()
    } : undefined;

    const messageRole = isGroupChatParticipants ? MessageRole.USER_CHAT : MessageRole.USER;
    const userGenerationId = uuidv4();
    const userMessage: Message = {
      recordId: userGenerationId,
      role: messageRole,
      senderId: useCommonStore.getState().user?.id,
      generations: [
        {
          id: userGenerationId,
          content: content,
          createdDate: Date.now(),
          attachments: attachments,
          ...(parentMessage && { parentMessage }),
        },
      ],
    };
    const assistantMessageId = uuidv4();
    let conversationIdToUse = activeConversationId;

    if (!conversationIdToUse) {
      conversationIdToUse = newConversationId ?? uuidv4();
      const newConvState = initialConversationState(conversationIdToUse, DEFAULT_CONVERSATION_NAME); // Temporary title
      newConvState.messages = [userMessage];
      newConvState.isCurrentMessageLoading = true;
      newConvState.isCurrentResponseGenerating = true;
      newConvState.modelName = selectedModel?.id;
      newConvState.allFileIds = newFileIds;
      newConvState.streamingAssistantMessageId = assistantMessageId;
      newConvState.toolsState = isFeatureSelected ?? initialFeaturesState;

      set((state) => ({
        activeConversationId: conversationIdToUse,
        conversations: {
          ...state.conversations,
          [conversationIdToUse!]: newConvState,
        },
      }));

      const isoDate = new Date().toISOString();
      const newSidebarEntry: ProjectItem = {
        ProjectId: projectId,
        ConversationId: conversationIdToUse!,
        ConversationTitle: newConvState.title,
        CreatedBy: '',
        CreatedDate: isoDate,
        LastUpdatedDate: isoDate,
        TenantId: '',
        Conversations: [
          {
            id: conversationIdToUse!,
            title: newConvState.title,
            createdDate: isoDate,
            lastUpdatedDate: isoDate,
            type: MessageRole.USER,
            content: userMessage.generations[0].content
          }
        ],
      };
      get().appendProjectItemToSidebarProjectsList(newSidebarEntry, projectId);
    } else {
      get()._addMessageToActiveConversation(userMessage);

      // if (conversationIdToUse) {
      //   get()._moveConversationToToday(conversationIdToUse);
      // }

      const currentConv = get().conversations[conversationIdToUse!];
      const prevAllFileIds = currentConv.allFileIds ?? [];
      updatedAllFileIds = [...prevAllFileIds, ...newFileIds];

      set((state) => ({
        conversations: {
          ...state.conversations,
          [conversationIdToUse!]: {
            ...state.conversations[conversationIdToUse!],
            isCurrentMessageLoading: true,
            isCurrentResponseGenerating: true,
            modelName: selectedModel?.id,
            allFileIds: updatedAllFileIds,
            streamingAssistantMessageId: assistantMessageId,
            toolsState: isFeatureSelected ?? initialFeaturesState,
            isAnalyzing: !!attachments && attachments.length > 0, // Set analyzing to true when files are uploaded

          },
        },
      }));
    }

    const newAssistantMessage: Message = {
      recordId: assistantMessageId,
      userRecordId: userMessage.generations[0].id,
      role: MessageRole.ASSISTANT,
      toolsStatus: isFeatureSelected?.[ButtonIds.SEARCH]
        ? {
          [Tools.SEARCH]: true,
          [Tools.CREATE_IMAGE]: false,
          [Tools.VOICE]: false,
        }
        : undefined,
      generations: [
        {
          id: assistantMessageId,
          content: '',
          createdDate: Date.now(),
          HasFileGeneratedResponse: false,
          isThinking: !!selectedModel?.isThinkingModel && !selectedModel?.isHarmonicModel && !selectedModel?.hasDynamicThinking,
          isAnalyzing: !!attachments && attachments.length > 0, // Set analyzing to true when files are uploaded
          usedModelId: selectedModel?.id,
        },
      ],
    };
    get()._addMessageToActiveConversation(newAssistantMessage, userMessage.generations[0].id);

    trackEvent(EVENTS.MESSAGE_SENT(conversationIdToUse, userMessage?.generations[0].content.length ?? 0));
    if (isFeatureSelected?.[ButtonIds.SEARCH]) {
      trackEvent(EVENTS.FEATURE_USED(FeatureName.SEARCH));
    }
    if (isFeatureSelected?.[ButtonIds.CREATE_IMAGE]) {
      trackEvent(EVENTS.FEATURE_USED(FeatureName.CREATE_IMAGE));
    }

    // if (isGroupChatParticipants) {
    //   const replyMessage = get().replyMessage;


    const attachmentsIds = userMessage.generations[0].attachments?.map((file) => file.fileId);
    const attachmentsMetadata = await get().fetchAttachmentMetadata(attachmentsIds || [], MessageRole.USER, config);
    const mentions = [
      selectedModel?.id
    ]
    const imageGeneration = isFeatureSelected?.[ButtonIds.CREATE_IMAGE] ?? false;
    const searchOnline = isFeatureSelected?.[ButtonIds.SEARCH] ?? false;
    const integrationSources = sources ? sources : [];

    console.log('sources in sources', sources, "searchOnline", searchOnline, "imageGeneration", imageGeneration)
    get().updateProjectConversation(conversationIdToUse, config);
    get().sendGroupChatStream(content, config, conversationIdToUse, attachmentsMetadata, replyMessage, mentions as string[], false, undefined, imageGeneration, integrationSources ?? [], searchOnline, undefined, userGenerationId);

    // get().sendGroupChatStream(content, config, conversationIdToUse, userMessage.generations[0].attachments as unknown as FileMetadata[], replyMessage);
    return;
    // } else {
    //   get().sendSSEMessage(
    //     JSON.stringify({
    //       question: userMessage.generations[0].content,
    //       conversation: [],
    //       file_ids: isFeatureSelected?.[ButtonIds.SEARCH] ? [] : updatedAllFileIds,
    //       filter_key_value_pair: [],
    //       model_name: selectedModel?.id,
    //       is_regenerate: false,
    //       conversation_id: conversationIdToUse,
    //       curr_file_ids: userMessage.generations[0].attachments?.map((file) => file.fileId),
    //       search_online_v2: isFeatureSelected?.[ButtonIds.SEARCH] ?? false,
    //       image_generation: isFeatureSelected?.[ButtonIds.CREATE_IMAGE] ?? false,
    //       record_id: recordId,
    //       msg_id: uuidv4(),
    //       assistant_message_id: assistantMessageId,
    //     }),
    //     config,
    //     conversationIdToUse
    //   );
    // }
  },


  setItemIdFilePathMap: (key: string, value: string) => {
    set((state) => ({
      itemIdFilePathMap: state.itemIdFilePathMap.set(key, value),
    }));
  },
  clearItemIdFilePathMap: () => {
    set(() => ({
      itemIdFilePathMap: new Map<string, string>(),
    }));
  },

  _addMessageToActiveConversation: (message, recordIdToUpdate?: string) => {
    const { activeConversationId } = get();
    if (!activeConversationId) return;
 
    set((state) => {
      const currentConv = state.conversations[activeConversationId];
      if (!currentConv) return {};
 
      const newMessages = currentConv.messages?.length ? [...currentConv.messages] : [];
      // console.log('newMessages in _addMessageToActiveConversation', newMessages)
 
      // Check for duplicate system messages by recordId
      if (message.role === MessageRole.CONVERSATION_TITLE_UPDATED) {
        const existingSystemMessage = newMessages.find(
          (m) => m.role === MessageRole.CONVERSATION_TITLE_UPDATED && m.recordId === message.recordId
        );
        if (existingSystemMessage) {
          return state;
        }
      }
      if (message.role === MessageRole.CONVERSATION_CREATED) {
        const existingSystemMessage = newMessages.find(
          (m) => m.role === MessageRole.CONVERSATION_CREATED && m.recordId === message.recordId
        );
        if (existingSystemMessage) {
          return state;
        }
        // Add CONVERSATION_CREATED message immediately after duplicate check
        newMessages.push(message);
      }
      if (message.role === MessageRole.ADD_PARTICIPANT) {
        const existingSystemMessage = newMessages.find(
          (m) => m.role === MessageRole.ADD_PARTICIPANT && m.recordId === message.recordId
        );
        if (existingSystemMessage) {
          return state;
        }
 
      }
 
      // Skip processing if message was already added (CONVERSATION_CREATED)
   
 
      if (message.role === MessageRole.ASSISTANT && recordIdToUpdate) {
        const existingIndex = newMessages.findIndex(
          (m) => m.role === MessageRole.ASSISTANT && m.userRecordId === recordIdToUpdate
        );
        if (existingIndex !== -1) {
          const prevMessage = newMessages[existingIndex];
          const totalGenerations = prevMessage.generations.length + message.generations.length;
          const updatedMessage: Message = {
            ...prevMessage,
            generations: [...prevMessage.generations, ...message.generations],
            activeGenerationIndex: totalGenerations - 1,
          };
          newMessages[existingIndex] = updatedMessage;
        } else {
          // Check for duplicate by recordId or generation ID before adding
          const isDuplicate = newMessages.some((m) => {
            if (m.recordId === message.recordId) return true;
            if (message.generations.length > 0) {
              return m.generations.some(gen =>
                message.generations.some(msgGen => gen.id === msgGen.id)
              );
            }
            return false;
          });
          if (!isDuplicate) {
            newMessages.push(message);
          }
        }
      } else {
        // Check for duplicate by recordId and generation IDs before adding
        // This prevents duplicates when multiple users send messages simultaneously
        const isDuplicate = newMessages.some((m) => {
          if (m.recordId === message.recordId) return true;
          // Also check generation IDs to catch duplicates from different sources
          if (message.generations && message.generations.length > 0 && m.generations && m.generations.length > 0) {
            return message.generations.some(msgGen =>
              m.generations.some(gen => gen.id === msgGen.id)
            );
          }
          return false;
        });
        if (!isDuplicate) {
          newMessages.push(message);
        }
      }
 
      return {
        conversations: {
          ...state.conversations,
          [activeConversationId]: {
            ...currentConv,
            messages: newMessages,
          },
        },
      };
    });
  },
 
 
 
 

  updateProjectNameInStore: (projectId: string, newName: string) => {
    set((state) => {
      const updatedProjectsList = state.sidebarProjectsList.map((project) =>
        project.id === projectId
          ? {
            ...project,
            name: newName,
          }
          : project
      );
      return { sidebarProjectsList: updatedProjectsList };
    });
  },

  fetchAndUpdateProjectAllConversations: async (configurationSettings: ConfigurationSettings, projectId: string) => {
    const { sidebarProjectsList } = get();
    const project = sidebarProjectsList.find((p) => p.id === projectId);
    if (!project) return;

    try {
      const response = await fetchProjectConversations(configurationSettings, projectId);
      const updatedProject = {
        ...project,
        items: response,
      };

      set((state) => {
        const updatedProjectsList = state.sidebarProjectsList.map((p) =>
          p.id === projectId ? updatedProject : p
        );
        return { sidebarProjectsList: updatedProjectsList };
      });
    } catch (error) {
      console.log(error);
      throw error;
    }
  },

  updateConversationTitleInStore: (conversationId: string, newTitle: string) => {
    console.log('updateConversationTitleInStore called', { conversationId, newTitle });
    set((state) => {
      const updatedConversations = state.conversations[conversationId]
        ? {
          ...state.conversations,
          [conversationId]: {
            ...state.conversations[conversationId],
            title: newTitle,
          },
        }
        : state.conversations;

      const updatedSidebarList = state.sidebarConversationsList.map((item) => {
        if (item.ConversationId === conversationId) {
          console.log('Updating sidebar item', item.ConversationId);
          return {
            ...item,
            ConversationTitle: newTitle,
          };
        }
        return item;
      });

      const updatedSearchList = state.searchConversationsList.map((item) => {
        if (item.ConversationId === conversationId) {
          return {
            ...item,
            ConversationTitle: newTitle,
          };
        }
        return item;
      });

      const updatedProjectsList = state.sidebarProjectsList.map((project) => ({
        ...project,
        items: project.items?.map((conv) => {
          if (conv.ConversationId === conversationId) {
            console.log('Updating project conversation item', conv.ConversationId);
            return { ...conv, ConversationTitle: newTitle };
          }
          return conv;
        }),
      }));

      // Update headerTitle if the renamed conversation is the active one
      const isHeaderTitleUpdateNeeded = state.activeConversationId === conversationId;
      if (isHeaderTitleUpdateNeeded) {
        console.log('Updating headerTitle to', newTitle);
      }

      return {
        conversations: updatedConversations,
        sidebarConversationsList: updatedSidebarList,
        searchConversationsList: updatedSearchList,
        sidebarProjectsList: updatedProjectsList,
        headerTitle: isHeaderTitleUpdateNeeded ? newTitle : state.headerTitle,
      };
    });
  },

  generateConversationTitle: async (conversationId: string, config: ConfigurationSettings) => {
    try {
      const response = await getTitle2(conversationId, config);
      const title = response.conversationTitle;
      get().setHeaderTitle(title as string);
      get().updateConversationTitleInStore(conversationId, title as string);
    } catch (error) {
      console.log(error);
      trackEvent(EVENTS.ERROR(error, {
        endpoint: `${config.paths.taskforce.collaborationConversation}/${conversationId}/title/generate`,
      }));
    }
  },


  updateProjectConversation: async (conversationId: string, configurationSettings: ConfigurationSettings) => {
    const sidebarProjectsList = get().sidebarProjectsList;
    const project = sidebarProjectsList.find((project) => project.items?.some((item) => item.ConversationId === conversationId))
    if (project) {
      // const payload = {
      //   Id: project.id,
      //   Name: project.name,
      //   Exttra: {},
      //   Conversations: [
      //     ...(project.items?.map((item) => item.ConversationId) || []),
      //     conversationId,
      //   ],
      // };
      const payload = {
        projectId: project.id
      };
      try {
        const baseUrl = `https://${configurationSettings.paths.taskforceBaseUrl}`;
        const url = `${baseUrl}${configurationSettings.paths.taskforce.collaborationConversation}/${conversationId}`;
        await clients.put(url, configurationSettings.blocksKey, JSON.stringify(payload));
      } catch (error) {
        console.log(error);
      }
    }
  },

  setrelatedSearcheQuestionsList: (conversationId: string, relatedSearcheQuestionsList: RelativeSearchQuestion[], messageId?: string) => {
    set((state) => {
      const updatedConversations = { ...state.conversations };
      if (updatedConversations[conversationId]) {
        const messages = updatedConversations[conversationId].messages;
        // Use messageId (recordId) if provided, otherwise fall back to streamingAssistantMessageId
        const targetMessageId = messageId || updatedConversations[conversationId].streamingAssistantMessageId;

        const updatedMessages = messages.map((message) => {
          if (message.role === 'assistant') {
            // Check if this message's recordId matches the target messageId
            const isTargetMessage = message.recordId === targetMessageId;

            return {
              ...message,
              generations: message.generations.map((generation) => {
                // Set relatedSearcheQuestionsList only for the target message
                // Match by message recordId or generation id (for backward compatibility)
                if (isTargetMessage || generation.id === targetMessageId) {
                  return {
                    ...generation,
                    relatedSearcheQuestionsList,
                    isSkeletonShowForQuestions: false,
                  };
                }
                return generation;
              }),
            };
          }
          return message;
        });

        return {
          conversations: {
            ...updatedConversations,
            [conversationId]: {
              ...updatedConversations[conversationId],
              messages: updatedMessages,
              isCurrentResponseGenerating: false,
            },
          },
        };
      }
      return state;
    });
  },

  setrelatedSearcheImageList: (conversationId: string, relatedSearcheImageList: RelativeSearchQuestion[], messageId?: string) => {
    // console.log('relatedSearcheImageList in setrelatedSearcheImageList', relatedSearcheImageList);
    set((state) => {
      const updatedConversations = { ...state.conversations };
      if (updatedConversations[conversationId]) {
        const messages = updatedConversations[conversationId].messages;
        // Use messageId (recordId) if provided, otherwise fall back to streamingAssistantMessageId
        const targetMessageId = messageId || updatedConversations[conversationId].streamingAssistantMessageId;
        // console.log('targetMessageId in setrelatedSearcheImageList', targetMessageId);


        const updatedMessages = messages.map((message) => {
          if (message.role === 'assistant') {
            // Check if this message's recordId matches the target messageId
            const isTargetMessage = message.recordId === targetMessageId;

            return {
              ...message,
              generations: message.generations.map((generation) => {
                // Set relatedSearcheImageList only for the target message
                // Match by message recordId or generation id (for backward compatibility)
                if (isTargetMessage || generation.id === targetMessageId) {
                  return {
                    ...generation,
                    relatedSearcheImageList,
                    isSkeletonShowForImages: false,
                  };
                }
                return generation;
              }),
            };
          }
          return message;
        });

        return {
          conversations: {
            ...updatedConversations,
            [conversationId]: {
              ...updatedConversations[conversationId],
              messages: updatedMessages,
            },
          },
        };
      }
      return state;
    });
  },

  setrelatedSearcheVideoList: (conversationId: string, relatedSearcheVideoList: RelativeSearchQuestion[], messageId?: string) => {
    set((state) => {
      const updatedConversations = { ...state.conversations };
      if (updatedConversations[conversationId]) {
        const messages = updatedConversations[conversationId].messages;
        // Use messageId (recordId) if provided, otherwise fall back to streamingAssistantMessageId
        const targetMessageId = messageId || updatedConversations[conversationId].streamingAssistantMessageId;

        const updatedMessages = messages.map((message) => {
          if (message.role === 'assistant') {
            // Check if this message's recordId matches the target messageId
            const isTargetMessage = message.recordId === targetMessageId;

            return {
              ...message,
              generations: message.generations.map((generation) => {
                // Set relatedSearcheVideoList only for the target message
                // Match by message recordId or generation id (for backward compatibility)
                if (isTargetMessage || generation.id === targetMessageId) {
                  return {
                    ...generation,
                    relatedSearcheVideoList,
                    isSkeletonShowForVideos: false,
                  };
                }
                return generation;
              }),
            };
          }
          return message;
        });

        return {
          conversations: {
            ...updatedConversations,
            [conversationId]: {
              ...updatedConversations[conversationId],
              messages: updatedMessages,
            },
          },
        };
      }
      return state;
    });
  },

  _handleWebSocketMessage: (parsedResponse: any, config: ConfigurationSettings) => {
    const { conversations } = get();



    const SSEResponse = WebsocketServices.SSEResponseParser(parsedResponse);
    // console.log('SSEResponse in _handleWebSocketMessage', SSEResponse);


    // CODE_EXECUTION_STARTED
    const receivedConversationId = SSEResponse.conversation_id;
    // console.log('receivedConversationId in _handleWebSocketMessage', receivedConversationId);


    if (!receivedConversationId || !conversations[receivedConversationId]) {
      console.warn('Received WS message but no conversation exist with this ID.');
      return;
    }
    const currentConv = conversations[receivedConversationId];
    currentConv.error = null;

    // send sse event to posthog
    posthogSSEEvent(SSEResponse.type, { conversationId: receivedConversationId });


    switch (SSEResponse.type) {

      case SSEResponseTypes.STREAM_CANCELLED: {
        const updatedMessages = getUpdatedMessages(SSEResponse, currentConv);

        set((state) => ({
          conversations: {
            ...state.conversations,
            [receivedConversationId]: {
              ...currentConv,
              isCancelOnGoing: false,
              messages: updatedMessages.length > 0 ? updatedMessages : currentConv.messages,
            },
          },
        }));
        break;
      }

      case SSEResponseTypes.ASSISTANT_REQUEST_QUEUED: {
        const data: AssistantPositionRemainingType = SSEResponse.data as AssistantPositionRemainingType;

        set((state) => ({
          conversations: {
            ...state.conversations,
            [receivedConversationId]: {
              ...currentConv,
              assistantResponseRemaining: data.remaining,
            },
          },
        }));
        break;
      }
      case SSEResponseTypes.ASSISTANT_REQUEST_PROCESSING_STARTED: {
        // No longer creating MODEL_THINKING messages - skip this event
        break;
      }
      case SSEResponseTypes.CODE_EXECUTION_STARTED: {
        const updatedMessages = getUpdatedMessages(SSEResponse, currentConv);

        set((state) => ({
          conversations: {
            ...state.conversations,
            [receivedConversationId]: {
              ...currentConv,
              isAnalyzing: true,
              messages: updatedMessages.length > 0 ? updatedMessages : currentConv.messages,
            },
          },
        }));
        break;
      }
      case SSEResponseTypes.CODE_EXECUTION_STEP: {
        // console.log('SSEResponseTypes.CODE_EXECUTION_STEP', currentConv);
        const updatedMessages = getUpdatedMessages(SSEResponse, currentConv);

        // Only update isAnalyzing when LLM answer isAnalyzed=true and analyzing=false
        const shouldUpdateAnalyzingState = currentConv.isAnalyzed === true && currentConv.isAnalyzing === false;

        set((state) => ({
          conversations: {
            ...state.conversations,
            [receivedConversationId]: {
              ...currentConv,
              isAnalyzing: shouldUpdateAnalyzingState ? false : currentConv.isAnalyzing,
              messages: updatedMessages.length > 0 ? updatedMessages : currentConv.messages,
            },
          },
        }));
        break;
      }
      case SSEResponseTypes.END_OF_STREAM: {
        let updatedActiveStreamsMap : Map<string, boolean> | undefined = undefined;
        if (SSEResponse.assistant_message_id) {
          const assistantMessage = currentConv.messages.find(
            (msg) =>
              msg.role === MessageRole.ASSISTANT &&
              msg.generations.some(
                (gen) => gen.id === SSEResponse.assistant_message_id
              )
          );
          if(assistantMessage?.userRecordId){
            updatedActiveStreamsMap = new Map(currentConv.activeStreamsMap);
            updatedActiveStreamsMap.delete(assistantMessage.userRecordId);
          }
        }
        set((state) => {
          return {
            conversations: {
            ...state.conversations,
            [receivedConversationId]: {
              ...currentConv,
              isCurrentMessageLoading: false,
              // isCurrentResponseGenerating: false,
              isAnalyzing: false,
              ...(updatedActiveStreamsMap && { activeStreamsMap: updatedActiveStreamsMap }),
              isCancelOnGoing: false,
            },
          },
        }
      });
        // Get the LATEST conversation state to check participants (not the stale currentConv)
        const latestConv = get().conversations[receivedConversationId];
        const hasParticipants = latestConv?.participants && latestConv.participants.length > 0;
        const isGroupChat = latestConv?.isGroupChat === true;
        const isIncognito = get().isIncognito;
        // Only generate title for non-group chats with less than 5 messages
        if (latestConv?.messages?.length < 5 && !hasParticipants && !isGroupChat && !isIncognito) {
          get().generateConversationTitle(receivedConversationId, config);
        }

        if (!hasParticipants && !isGroupChat) {
          const wasSearchEnabled = currentConv && currentConv.toolsState ? currentConv.toolsState[ButtonIds.SEARCH] : false;

          const currentStreamingMessage = currentConv?.messages?.find(
            (m) => m.role === 'assistant' &&
              m.generations.some((g) => g.id === SSEResponse.assistant_message_id)
          );
          const currentGeneration = currentStreamingMessage?.generations?.find(
            (g) => g.id === SSEResponse.assistant_message_id
          );
          const isSearchSourcesShow = currentGeneration?.isSearchSourcesShow ?? false;

          // Use the message's recordId instead of streamingAssistantMessageId (which is a generation id)
          const messageIdToUse = currentStreamingMessage?.recordId ?? SSEResponse.assistant_message_id ?? '';

          set((state) => ({
            conversations: {
              ...state.conversations,
              [receivedConversationId]: {
                ...state.conversations[receivedConversationId],
                isCurrentResponseGenerating: (wasSearchEnabled || isSearchSourcesShow) ? true : false,
                streamingAssistantMessageId: null,
                isCancelOnGoing: false,
                messages: state?.conversations[receivedConversationId].messages.map((message) => {
                  if (message.role === 'assistant') {
                    return {
                      ...message,
                      generations: message.generations.map((generation) => {
                        if (generation.id === SSEResponse.assistant_message_id) {
                          return {
                            ...generation,
                            isAnalyzing: false,  // Stop analyzing when stream ends
                            isThinking: false,
                            isThisGenerationOnGoing: false,
                            isSkeletonShowForImages: (wasSearchEnabled || isSearchSourcesShow) ? true : false,
                            isSkeletonShowForVideos: (wasSearchEnabled || isSearchSourcesShow) ? true : false,
                            isSkeletonShowForQuestions: (wasSearchEnabled || isSearchSourcesShow) ? true : false
                          };
                        }
                        return generation;
                      }),
                    };
                  }
                  return message;
                }),
              },
            },
          }));

          // console.log('wasSearchEnabled', wasSearchEnabled,'isSearchSourcesShow', isSearchSourcesShow);
          if (wasSearchEnabled || isSearchSourcesShow) {
            // console.log('inside if wasSearchEnabled', wasSearchEnabled, 'isSearchSourcesShow', isSearchSourcesShow);

            get().getSearchRelatedImageList({
              conversationId: receivedConversationId,
              messageId: messageIdToUse,
              type: 'IMAGE_SUGGESTIONS', modelName: currentConv.modelName ?? ''
            },
              config);

            get().getSearchRelatedVideoList({
              conversationId: receivedConversationId,
              messageId: messageIdToUse,
              type: 'VIDEO_SUGGESTIONS',
              modelName: currentConv.modelName ?? ''
            },
              config);

            get().getSearchRelatedList({
              conversationId: receivedConversationId,
              messageId: messageIdToUse,
              type: 'SEARCH_SUGGESTIONS',
              modelName: currentConv.modelName ?? ''
            },
              config);
          }

        } else {
          // For group chats, only set isCurrentResponseGenerating to false if no other messages are still streaming
          set((state) => {
            const conv = state.conversations[receivedConversationId];
            if (!conv) return state;

            // Check if any other assistant message is still streaming (has empty content)
            const hasOtherStreamingMessages = conv.messages.some(
              (msg) =>
                msg.role === MessageRole.ASSISTANT &&
                msg.generations?.some(
                  (g) => g.id !== SSEResponse.assistant_message_id && (g?.isThisGenerationOnGoing ?? false)
                )
            );

            return {
              conversations: {
                ...state.conversations,
                [receivedConversationId]: {
                  ...conv,
                  isCancelOnGoing: false,
                  isCurrentResponseGenerating: hasOtherStreamingMessages,
                  streamingAssistantMessageId: hasOtherStreamingMessages ? conv.streamingAssistantMessageId : null,
                  messages: state?.conversations[receivedConversationId].messages.map((message) => {
                  if (message.role === 'assistant') {
                    return {
                      ...message,
                      generations: message.generations.map((generation) => {
                        if (generation.id === SSEResponse.assistant_message_id) {
                          return {
                            ...generation,
                            isAnalyzing: false,  // Stop analyzing when stream ends
                            isThinking: false,
                            isThisGenerationOnGoing: false,
                            // isSkeletonShowForImages: (wasSearchEnabled || isSearchSourcesShow) ? true : false,
                            // isSkeletonShowForVideos: (wasSearchEnabled || isSearchSourcesShow) ? true : false,
                            // isSkeletonShowForQuestions: (wasSearchEnabled || isSearchSourcesShow) ? true : false
                          };
                        }
                        return generation;
                      }),
                    };
                  }
                  return message;
                }),
                },
              },
            };
          });
        }


        // get().updateProjectConversation(receivedConversationId, config);
        break;
      }

      case SSEResponseTypes.CONTEXT_WINDOW_EXCEEDED: {
        const updatedMessagesForSuccess = getUpdatedMessages(SSEResponse, currentConv);
        if (updatedMessagesForSuccess.length > 0) {
          set((state) => ({
            conversations: {
              ...state.conversations,
              [receivedConversationId]: {
                ...currentConv,
                messages: updatedMessagesForSuccess,
                isCurrentMessageLoading: false,
                isCurrentResponseGenerating: false,
                isAnalyzing: false,
                error: 'CONTEXT_WINDOW_EXCEEDED_ERROR_MESSAGE',
              },
            },
          }));
        }
        break;
      }

      case SSEResponseTypes.IMAGE_GENERATION_LIMIT_EXCEEDED: {
        const updatedMessagesForSuccess = getUpdatedMessages(SSEResponse, currentConv);
        if (updatedMessagesForSuccess.length > 0) {
          set((state) => ({
            conversations: {
              ...state.conversations,
              [receivedConversationId]: {
                ...currentConv,
                messages: updatedMessagesForSuccess,
                isCurrentMessageLoading: false,
                isCurrentResponseGenerating: false,
                isAnalyzing: false,
                error: 'IMAGE_GENERATION_LIMIT_EXCEEDED_ERROR_MESSAGE',
              },
            },
          }));
        }
        break;
      }

      case SSEResponseTypes.RESPONSE_SOURCES:
      case SSEResponseTypes.SEARCH_SOURCES: {
        const updatedMessagesForSuccess = getUpdatedMessages(SSEResponse, currentConv);
        if (updatedMessagesForSuccess.length > 0) {
          set((state) => ({
            conversations: {
              ...state.conversations,
              [receivedConversationId]: {
                ...currentConv,
                messages: updatedMessagesForSuccess,
                streamingAssistantMessageId: SSEResponse.assistant_message_id
              },
            },
          }));
        }
        break;
      }

      case SSEResponseTypes.SERVER_ERROR: {
        const updatedMessagesForSuccess = getUpdatedMessages(SSEResponse, currentConv);
        if (updatedMessagesForSuccess.length > 0) {
          set((state) => ({
            conversations: {
              ...state.conversations,
              [receivedConversationId]: {
                ...currentConv,
                messages: updatedMessagesForSuccess,
                isCurrentMessageLoading: false,
                isCurrentResponseGenerating: false,
                isAnalyzing: false,
                error: 'SERVER_ERROR_MESSAGE',
                streamingAssistantMessageId: null
              },
            },
          }));
        }
        break;
      }
      case SSEResponseTypes.CONVERSATION_TITLE:
        get().updateConversationTitleInStore(receivedConversationId, SSEResponse.data as string);
        break;
      case SSEResponseTypes.FILE_GENERATION_SUCCESS:
      case SSEResponseTypes.IMAGE_GENERATION_SUCCESS:
      case SSEResponseTypes.CODE_EXECUTION_ARTIFACT: {
        // console.log('Processing CODE_EXECUTION_ARTIFACT case');
        const updatedMessagesForSuccess = getUpdatedMessages(SSEResponse, currentConv);
        // console.log('updatedMessagesForSuccess in case SSEResponseTypes.FILE_GENERATION_SUCCESS', updatedMessagesForSuccess);
        if (updatedMessagesForSuccess.length > 0) {
          set((state) => ({
            conversations: {
              ...state.conversations,
              [receivedConversationId]: {
                ...currentConv,
                messages: updatedMessagesForSuccess,
              },
            },
          }));
        }
        break;
      }
      case SSEResponseTypes.FILE_GENERATION_FAILED: {
        const updatedMessagesForSuccess = getUpdatedMessages(SSEResponse, currentConv);
        set((state) => ({
          conversations: {
            ...state.conversations,
            [receivedConversationId]: {
              ...currentConv,
              isCurrentMessageLoading: false,
              isCurrentResponseGenerating: false,
              isAnalyzing: false,
              error: 'FILE_GENERATION_FAILED_ERROR_MESSAGE',
              messages: updatedMessagesForSuccess,
            },
          },
        }));
        break;
      }
      case SSEResponseTypes.IMAGE_GENERATION_FAILED: {
        const updatedMessagesForSuccess = getUpdatedMessages(SSEResponse, currentConv);
        set((state) => ({
          conversations: {
            ...state.conversations,
            [receivedConversationId]: {
              ...currentConv,
              isCurrentMessageLoading: false,
              isCurrentResponseGenerating: false,
              isAnalyzing: false,
              error: currentConv.error,
              messages: updatedMessagesForSuccess,
            },
          },
        }));
        break;
      }
      case SSEResponseTypes.IMAGE_GENERATION_STARTED:
      case SSEResponseTypes.FILE_GENERATION_STARTED:
      case SSEResponseTypes.FILE_GENERATION_ON_PROGRESS:
      case SSEResponseTypes.LLM_ANSWER:
      case SSEResponseTypes.SEARCH_RESPONSE: {
        if (SSEResponseTypes.FILE_GENERATION_STARTED && Array.isArray(SSEResponse.data)) {
          return;
        }
        const updatedMessages = getUpdatedMessages(SSEResponse, currentConv);
        // console.log('updatedMessages in case SSEResponseTypes.IMAGE_GENERATION_STARTED', updatedMessages)


        if (updatedMessages.length == 0) return;

        // If currently analyzing, transition to analyzed state
        const shouldSetAnalyzed = currentConv.isAnalyzing;


        set((state) => ({
          conversations: {
            ...state.conversations,
            [receivedConversationId]: {
              ...currentConv,
              messages: updatedMessages,
              isAnalyzing: false,
              isAnalyzed: shouldSetAnalyzed,
              isCurrentMessageLoading: false,
              streamingAssistantMessageId: SSEResponse.assistant_message_id
            },
          },
        }));
        break;
      }

      case SSEResponseTypes.LLM_THINKING: {
        const updatedMessages = getUpdatedMessages(SSEResponse, currentConv);
        // console.log('updatedMessages', updatedMessages)

        // console.log('SSEResponse', SSEResponse, "receivedConversationId", receivedConversationId, "conversations", conversations, "currentConv", currentConv,"updatedMessages",updatedMessages);
        if (updatedMessages.length == 0) return;

        set((state) => ({
          conversations: {
            ...state.conversations,
            [receivedConversationId]: {
              ...currentConv,
              messages: updatedMessages,
              isAnalyzing: false,
              isCurrentMessageLoading: false,
              streamingAssistantMessageId: SSEResponse.assistant_message_id,

            },
          },
        }));
        break;
      }

      case SSEResponseTypes.RESPONSE_TIME_ANALYTICS: {
        const updatedMessages = getUpdatedMessages(SSEResponse, currentConv);
        if (updatedMessages.length == 0) return;

        set((state) => ({
          conversations: {
            ...state.conversations,
            [receivedConversationId]: {
              ...currentConv,
              messages: updatedMessages

            },
          },
        }));
        break;
      }

      case SSEResponseTypes.MODEL_SELECTED: {
        const updatedMessages = getUpdatedMessages(SSEResponse, currentConv);
        if (updatedMessages.length == 0) return;

        set((state) => ({
          conversations: {
            ...state.conversations,
            [receivedConversationId]: {
              ...currentConv,
              messages: updatedMessages

            },
          },
        }));
        break;
      }
    }
  },

  regenerateResponse: async (recordId: string, config: ConfigurationSettings) => {


    // console.log('recordId in regenerateResponse', config)
    const { activeConversationId } = get();
    const selectedModel = useModelStore.getState().selectedModel;

    if (!activeConversationId) return;

    console.log('recordId', recordId,

      "get().conversations[activeConversationId].messages", get().conversations[activeConversationId].messages
    )

    const userMessage = get().conversations[activeConversationId].messages.find(
      (msg) => (msg.role === MessageRole.USER) && msg.recordId === recordId
    );
    const prevAssistantMessage = get().conversations[activeConversationId].messages.find(
      (msg) => msg.role === 'assistant' && msg.userRecordId === recordId
    );

    // console.log('recordId in userMessage', userMessage, "prevAssistantMessage", prevAssistantMessage);

    // Validate that we found the user message - required for regeneration
    if (!userMessage || !userMessage.generations || !userMessage.generations[0] || !userMessage.generations[0].content) {
      console.error('Failed to find user message for regeneration with recordId:', recordId);
      set((state) => ({
        conversations: {
          ...state.conversations,
          [activeConversationId!]: {
            ...state.conversations[activeConversationId!],
            isCurrentMessageLoading: false,
            isCurrentResponseGenerating: false,
            error: 'Cannot regenerate: User message not found.',
          },
        },
      }));
      return;
    }
    const assistantMessageId = uuidv4();


    set((state) => ({
      conversations: {
        ...state.conversations,
        [activeConversationId!]: {
          ...state.conversations[activeConversationId!],
          isCurrentMessageLoading: true,
          isCurrentResponseGenerating: true,
          streamingAssistantMessageId: assistantMessageId,
        },
      },
    }));

    // Get the active user generation for parentUserGenerationId
    const activeUserGenerationIndex = userMessage.activeGenerationIndex ?? 0;
    const activeUserGeneration = userMessage.generations[activeUserGenerationIndex];

    const newAssistantMessage: Message = {
      recordId: assistantMessageId,
      userRecordId: userMessage?.generations[0]?.id,
      role: MessageRole.ASSISTANT,
      toolsStatus: userMessage?.toolsStatus,
      generations: [
        {
          id: assistantMessageId,
          content: '',
          createdDate: Date.now(),
          isThinking: !!selectedModel?.isThinkingModel && !selectedModel?.isHarmonicModel && !selectedModel?.hasDynamicThinking,
          hasSearchQuery: prevAssistantMessage?.toolsStatus?.[Tools.SEARCH] ?? false,
          usedModelId: selectedModel?.id,
          parentUserGenerationId: activeUserGeneration?.id,
          isThisGenerationOnGoing: true,
        },
      ],
    };
    get()._addMessageToActiveConversation(newAssistantMessage, userMessage?.generations[0]?.id);

    trackEvent(EVENTS.MESSAGE_SENT(activeConversationId, userMessage.generations[0].content.length ?? 0));
    if (prevAssistantMessage?.toolsStatus?.[Tools.SEARCH]) {
      trackEvent(EVENTS.FEATURE_USED(FeatureName.SEARCH));
    }
    if (prevAssistantMessage?.toolsStatus?.[Tools.CREATE_IMAGE]) {
      trackEvent(EVENTS.FEATURE_USED(FeatureName.CREATE_IMAGE));
    }

    const attachmentsIds = userMessage.generations[0].attachments?.map((file) => file.fileId);
    const attachmentsMetadata = await get().fetchAttachmentMetadata(attachmentsIds || [], MessageRole.USER, config);
    const imageGeneration = prevAssistantMessage?.toolsStatus?.[Tools.CREATE_IMAGE] ?? false;
    const searchOnline = prevAssistantMessage?.toolsStatus?.[Tools.SEARCH] ?? false;
    const integrationSources = prevAssistantMessage?.toolsStatus?.[Tools.SEARCH] ? [Tools.SEARCH] : [];

    const mentions = [
      selectedModel?.id
    ]

    
    get().sendGroupChatStream(userMessage.generations[0].content, config, activeConversationId, attachmentsMetadata, undefined, mentions as string[], true, prevAssistantMessage?.generations[0]?.id, imageGeneration, integrationSources, searchOnline, undefined, userMessage.generations[0].id);


    // get().sendSSEMessage(
    //   JSON.stringify({
    //     question: userMessage.generations[0].content,
    //     conversation: [],
    //     file_ids: get().conversations[activeConversationId].allFileIds,
    //     filter_key_value_pair: [],
    //     model_name: selectedModel?.id,
    //     is_regenerate: true,
    //     conversation_id: activeConversationId,
    //     curr_file_ids: userMessage?.generations[0].attachments?.map((file) => file.fileId),
    //     search_online_v2: prevAssistantMessage?.toolsStatus?.[Tools.SEARCH] ?? false,
    //     image_generation: prevAssistantMessage?.toolsStatus?.[Tools.CREATE_IMAGE] ?? false,
    //     record_id: recordId,
    //     msg_id: uuidv4(),
    //     assistant_message_id: assistantMessageId,
    //     original_regeneration_message_id: prevAssistantMessage?.generations[0]?.id,
    //   }),
    //   config,
    //   activeConversationId
    // );
  },

  sendMessage: async (
    content: string,
    config: ConfigurationSettings,
    newConversationId?: string,
    attachments?: Attachment[],
    isFeatureSelected?: Record<ButtonIds, boolean>,
    isGroupChatParticipants?: boolean,
    parentIds?: string[],
    sources?: string[],
    isRegenerate?: boolean,
    originalRegenerationMessageId?: string,
    mentions?: Mention[],
    parentUserGenerationId?: string,
    isIncognitoChat?: boolean,
    customGptId?: string,
  ) => {
    // console.log('mentions in sendMessage', mentions)
    const { activeConversationId, replyMessage } = get();

    const selectedModel = useModelStore.getState().selectedModel;
    const newFileIds = attachments?.map((item) => item.fileId) ?? [];
    let updatedAllFileIds = newFileIds;

    const parentMessage = replyMessage?.generation && replyMessage?.repliedUser ? {
      senderName: replyMessage.repliedUser.firstName + (replyMessage.repliedUser.lastName ? ` ${replyMessage.repliedUser.lastName}` : ''),
      content: replyMessage.generation.content,
      time: replyMessage.generation.createdDate || Date.now()
    } : undefined;
    const messageRole = isGroupChatParticipants ? MessageRole.USER_CHAT : MessageRole.USER;
    // console.log('messageRole in sendMessage', messageRole, "isGroupChatParticipants", isGroupChatParticipants)
    const userGenerationId = uuidv4();
    const userMessage: Message = {
      recordId: userGenerationId,
      role: messageRole,
      senderId: useCommonStore.getState().user?.id,
      // Set delivery status to PENDING for group chat messages
      ...(isGroupChatParticipants && { deliveryStatus: MessageDeliveryStatus.PENDING }),
      generations: [
        {
          id: userGenerationId,
          content: content,
          createdDate: Date.now(),
          attachments: attachments,
          ...(parentIds && parentIds.length > 0 && { parentIds: parentIds }),
          ...(parentMessage && { parentMessage }),
          ...(mentions && mentions.length > 0 && { mentions: mentions }),
        },
      ],
    };
    const assistantMessageId = uuidv4();
    let conversationIdToUse = activeConversationId;

    if (!conversationIdToUse) {

      conversationIdToUse = newConversationId ?? uuidv4();
      const newConvState = initialConversationState(conversationIdToUse, DEFAULT_CONVERSATION_NAME); // Temporary title
      newConvState.messages = [userMessage];
      newConvState.isCurrentMessageLoading = isGroupChatParticipants ? false : true;
      newConvState.isCurrentResponseGenerating = true;
      newConvState.modelName = selectedModel?.id;
      newConvState.allFileIds = newFileIds;
      // newConvState.streamingAssistantMessageId = assistantMessageId;
      newConvState.toolsState = isFeatureSelected ?? initialFeaturesState;
      if (customGptId) {
        newConvState.customGptId = customGptId;
      }

      set((state) => ({
        activeConversationId: conversationIdToUse,
        conversations: {
          ...state.conversations,
          [conversationIdToUse!]: newConvState,
        },
      }));

      if (isIncognitoChat) {
        console.log('incognito chat - not adding to sidebar');
        return;
      }

      const isoDate = new Date().toISOString();
      const currentUser = useCommonStore.getState().user;
      const newSidebarEntry: SidebarItem = {
        ConversationId: conversationIdToUse!,
        ConversationTitle: DEFAULT_CONVERSATION_NAME, // Temporary title, will be updated by _handleWebSocketMessage or _moveConversationToToday
        Conversations: [], // Assuming this is for potential sub-items, adjust if not used
        CreatedBy: currentUser?.id || '',
        CreatedDate: isoDate,
        LastUpdatedDate: isoDate,
        TenantId: '', // Populate if available
        IsGroup: isGroupChatParticipants ?? false,
        CustomGptId: customGptId,
      };
      get().appendToSidebarConversationsList(newSidebarEntry);
    } else {
      get()._addMessageToActiveConversation(userMessage);

      if (conversationIdToUse) {
        get()._moveConversationToToday(conversationIdToUse);
      }

      const currentConv = get().conversations[conversationIdToUse!];
      const prevAllFileIds = currentConv.allFileIds ?? [];
      updatedAllFileIds = [...prevAllFileIds, ...newFileIds];

      set((state) => ({
        conversations: {
          ...state.conversations,
          [conversationIdToUse!]: {
            ...state.conversations[conversationIdToUse!],
            isCurrentMessageLoading: isGroupChatParticipants ? false : true,
            isCurrentResponseGenerating: true,
            modelName: selectedModel?.id,
            allFileIds: updatedAllFileIds,
            streamingAssistantMessageId: assistantMessageId,
            toolsState: isFeatureSelected ?? initialFeaturesState,
            isAnalyzing: !!attachments && attachments.length > 0, // Set analyzing to true when files are uploaded

          },
        },
      }));
    }
    if (!isGroupChatParticipants) {
      get().setHasAddedAssistantAsParticipant(true);
      // }

      const newAssistantMessage: Message = {
        recordId: assistantMessageId,
        userRecordId: userMessage.generations[0].id,
        role: MessageRole.ASSISTANT,
        toolsStatus: isFeatureSelected?.[ButtonIds.SEARCH]
          ? {
            [Tools.SEARCH]: true,
            [Tools.CREATE_IMAGE]: false,
            [Tools.VOICE]: false,
          }
          : undefined,
        generations: [
          {
            id: assistantMessageId,
            content: '',
            createdDate: Date.now(),
            HasFileGeneratedResponse: false,
            isThinking: !!selectedModel?.isThinkingModel && !selectedModel?.isHarmonicModel && !selectedModel?.hasDynamicThinking,
            isAnalyzing: !!attachments && attachments.length > 0, // Set analyzing to true when files are uploaded
            isAnalyzed: false,
            usedModelId: selectedModel?.id,
            ...(parentUserGenerationId && { parentUserGenerationId }),
            isThisGenerationOnGoing: true,
          },
        ],
      };
      // get().appendToSidebarConversationsList(newAssistantMessage);
      get()._addMessageToActiveConversation(newAssistantMessage, userMessage.generations[0].id);
    }

    trackEvent(EVENTS.MESSAGE_SENT(conversationIdToUse, userMessage?.generations[0].content.length ?? 0));
    if (isFeatureSelected?.[ButtonIds.SEARCH]) {
      trackEvent(EVENTS.FEATURE_USED(FeatureName.SEARCH));
    }
    if (isFeatureSelected?.[ButtonIds.CREATE_IMAGE]) {
      trackEvent(EVENTS.FEATURE_USED(FeatureName.CREATE_IMAGE));
    }
    // if (isGroupChatParticipants) {
    const attachmentsIds = userMessage.generations[0].attachments?.map((file) => file.fileId);
    const attachmentsMetadata = await get().fetchAttachmentMetadata(attachmentsIds || [], MessageRole.USER, config);
    // const replyMessage = get().replyMessage;


    let mentionUserIds: string[];

    if (!isGroupChatParticipants) {
      mentionUserIds = selectedModel?.id ? [
        selectedModel.id
      ] : []
    } else if (isGroupChatParticipants && mentions && mentions.length > 0) {
      // Extract unique user IDs from Mention objects
      const userIds = new Set(mentions.map((m) => m.userId));
      mentionUserIds = Array.from(userIds);
    } else if (isGroupChatParticipants && !mentions) {
      const hasFeatureSelected = isFeatureSelected?.[ButtonIds.SEARCH] ||
        isFeatureSelected?.[ButtonIds.CREATE_IMAGE] ||
        isFeatureSelected?.[ButtonIds.VOICE];
      if (hasFeatureSelected) {
        mentionUserIds = selectedModel?.id ? [selectedModel.id] : []
        // console.log('mentionUserIds in sendMessageToStore', mentionUserIds)
      } else {
        mentionUserIds = [];
      }
    } else {
      mentionUserIds = [];
    }
    const imageGeneration = isFeatureSelected?.[ButtonIds.CREATE_IMAGE] ?? false;
    const searchOnline = isFeatureSelected?.[ButtonIds.SEARCH] ?? false;
    const integrationSources = sources ? sources : [];

    get().sendGroupChatStream(content, config, conversationIdToUse, attachmentsMetadata, replyMessage, mentionUserIds, isRegenerate ?? false, originalRegenerationMessageId ?? undefined, imageGeneration, integrationSources ?? [], searchOnline, undefined, userGenerationId);
    return;


  },

  retryFailedMessage: async (
    messageId: string,
    config: ConfigurationSettings,
    conversationId: string,
  ) => {
    const { conversations } = get();
    const conv = conversations[conversationId];
    if (!conv || !conv.messages) return;

    const isGroupChatParticipants = conv?.participants && conv.participants.length > 0;

    // Find the failed message
    const failedMessage = conv.messages.find(
      (msg) => msg.recordId === messageId && msg.deliveryStatus === MessageDeliveryStatus.FAILED
    );

    if (!failedMessage || failedMessage.role !== MessageRole.USER_CHAT) return;

    const generation = failedMessage.generations[0];
    if (!generation || !generation.content) return;

    // Update message status to PENDING
    set((state) => {
      const currentConv = state.conversations[conversationId];
      if (!currentConv) return state;

      const updatedMessages = currentConv.messages.map((msg) => {
        if (msg.recordId === messageId) {
          return {
            ...msg,
            deliveryStatus: MessageDeliveryStatus.PENDING,
          };
        }
        return msg;
      });

      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...currentConv,
            messages: updatedMessages,
            isCurrentMessageLoading: isGroupChatParticipants ? false : true,
            isCurrentResponseGenerating: true,
          },
        },
      };
    });

    // Extract message data
    const content = generation.content;
    const attachments = generation.attachments || [];
    const mentions = generation.mentions || [];
    // const parentIds = generation.parentIds || [];

    // Convert attachments to FileMetadata format
    const attachmentsMetadata: FileMetadata[] = attachments.map((att) => ({
      fileId: att.fileId,
      fileName: att.fileName,
      extension: att.extension,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      size: 0, // Size not available from Attachment
      sender: att.sender,
    }));

    // Extract mention user IDs
    const mentionUserIds = mentions.map((m) => m.userId);

    // Retry sending the message - pass retryMessageId to prevent creating a new message
    try {
      await get().sendGroupChatStream(
        content,
        config,
        conversationId,
        attachmentsMetadata,
        null, // replyMessage
        mentionUserIds,
        false, // isRegenerate
        undefined, // originalRegenerationMessageId
        false, // imageGeneration
        [], // integrationSources
        false, // searchOnline
        messageId, // retryMessageId - tells sendGroupChatStream to update existing message instead of creating new one
        messageId // dummyUserMessageId - the dummy ID that needs to be replaced with server-assigned ID
      );
    } catch (error) {
      // Error handling is already done in sendGroupChatStream
      console.error('Failed to retry message:', error);
    }
  },


  temporaryConversation: (
    content: string,
    attachments?: Attachment[],
    isFeatureSelected?: Record<ButtonIds, boolean>
  ) => {
    const selectedModel = useModelStore.getState().selectedModel;
    const newFileIds = attachments?.map((item) => item.fileId) ?? [];
    const temporaryConversationId = uuidv4();

    const userGenerationId = uuidv4();
    const userMessage: Message = {
      recordId: userGenerationId,
      role: MessageRole.USER,
      senderId: useCommonStore.getState().user?.id,
      generations: [
        {
          id: userGenerationId,
          content: content,
          createdDate: Date.now(),
          attachments: attachments,
        },
      ],
    };

    const assistantMessageId = uuidv4();
    const newConvState = initialConversationState(temporaryConversationId, DEFAULT_CONVERSATION_NAME);
    newConvState.messages = [userMessage];
    newConvState.isCurrentMessageLoading = true;
    newConvState.isCurrentResponseGenerating = true;
    newConvState.modelName = selectedModel?.id;
    newConvState.allFileIds = newFileIds;
    newConvState.streamingAssistantMessageId = assistantMessageId;
    newConvState.toolsState = isFeatureSelected ?? initialFeaturesState;

    // Add assistant message placeholder
    const newAssistantMessage: Message = {
      recordId: assistantMessageId,
      userRecordId: userMessage.generations[0].id,
      role: MessageRole.ASSISTANT,
      toolsStatus: isFeatureSelected?.[ButtonIds.SEARCH]
        ? {
          [Tools.SEARCH]: true,
          [Tools.CREATE_IMAGE]: false,
          [Tools.VOICE]: false,
        }
        : undefined,
      generations: [
        {
          id: assistantMessageId,
          content: '',
          createdDate: Date.now(),
          HasFileGeneratedResponse: false,
          isThinking: !!selectedModel?.isThinkingModel && !selectedModel?.isHarmonicModel && !selectedModel?.hasDynamicThinking,
          isAnalyzing: !!attachments && attachments.length > 0,
          isAnalyzed: false,
          usedModelId: selectedModel?.id,
          isThisGenerationOnGoing: true,
        },
      ],
    };
    newConvState.messages.push(newAssistantMessage);

    set((state) => ({
      activeConversationId: temporaryConversationId,
      conversations: {
        ...state.conversations,
        [temporaryConversationId]: newConvState,
      },
    }));

    return { temporaryConversationId, recordId: userMessage.generations[0].id, assistantMessageId };
  },

  replaceConversationId: (
    oldConversationId: string,
    newConversationId: string,
    isIncognitoChat: boolean
  ) => {
    set((state) => {
      const oldConversation = state.conversations[oldConversationId];

      if (!oldConversation) {
        console.warn(`Conversation ${oldConversationId} not found for replacement`);
        return {};
      }

      const newConversation: ConversationState = {
        ...oldConversation,
        id: newConversationId,
      };

      const updatedActiveConversationId =
        state.activeConversationId === oldConversationId
          ? newConversationId
          : state.activeConversationId;

      const remainingConversations = { ...state.conversations };
      delete remainingConversations[oldConversationId];

      // Incognito chat - do not update sidebar
      if (isIncognitoChat) {
        return {
          activeConversationId: updatedActiveConversationId,
          conversations: {
            ...remainingConversations,
            [newConversationId]: newConversation,
          },
        };
      }

      // Normal chat - update sidebar
      const sidebarItemIndex = state.sidebarConversationsList.findIndex(
        (item) => item.ConversationId === oldConversationId
      );

      let updatedSidebarList = [...state.sidebarConversationsList];

      if (sidebarItemIndex !== -1) {
        // Update existing sidebar entry with new conversationId
        updatedSidebarList[sidebarItemIndex] = {
          ...updatedSidebarList[sidebarItemIndex],
          ConversationId: newConversationId,
        };
      } else {
        // If not found in sidebar, add new entry
        const isoDate = new Date().toISOString();
        const currentUser = useCommonStore.getState().user;
        const newSidebarEntry: SidebarItem = {
          ConversationId: newConversationId,
          ConversationTitle: oldConversation.title || DEFAULT_CONVERSATION_NAME,
          Conversations: [],
          CreatedBy: currentUser?.id || '',
          CreatedDate: isoDate,
          LastUpdatedDate: isoDate,
          TenantId: '',
        };
        updatedSidebarList = [newSidebarEntry, ...updatedSidebarList];
      }

      return {
        activeConversationId: updatedActiveConversationId,
        conversations: {
          ...remainingConversations,
          [newConversationId]: newConversation,
        },
        sidebarConversationsList: updatedSidebarList,
      };
    });
  },




  setActiveConversationId: (conversationId) => {
    // Sync hasParticipantsArray from cache when switching conversations
    const cachedParticipants = conversationId
      ? get().hasGroupChatParticipantsArray[conversationId]
      : undefined;

    set((state) => ({
      activeConversationId: conversationId,
      hasParticipantsArray: cachedParticipants || [],
      hasParticipant: cachedParticipants && cachedParticipants.length > 0,
      ...(conversationId && {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...(state.conversations[conversationId] || initialConversationState(conversationId)),
            ...(cachedParticipants !== undefined && {
              participants: cachedParticipants || [],
            }),
            // Ensure modelName is always set
            modelName: state.conversations[conversationId]?.modelName || useModelStore.getState().selectedModel?.id || 'auto',
            // Set isRecentlyAdded to false when user clicks on the conversation
            isRecentlyAdded: false,
          },
        },
      }),
      hasAddedAssistantAsParticipant: !cachedParticipants || cachedParticipants.length === 0,
    }));
  },
  setActiveConversationModelName: (modelName: string) => {
    const conversationId = get().activeConversationId;
    if (!conversationId) return;

    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...(state.conversations[conversationId] || initialConversationState(conversationId)),
          modelName,
        },
      },
    }));
  },
  setActiveAudioMessageId: (messageId) => {
    set({
      activeAudioMessageId: messageId,
    });
  },
  setActiveConversation: async (conversationId, config: ConfigurationSettings, participants?: IamData[]) => {
    // console.log('conversationId in: setActiveConversation', conversationId)
    if (conversationId) {
      // Sync participants from cache or provided participants
      const cachedParticipants = get().hasGroupChatParticipantsArray[conversationId];
      const participantsToUse = participants || cachedParticipants || [];

      // Update hasParticipantsArray if we have participants
      if (participantsToUse.length > 0 || cachedParticipants !== undefined) {
        set({
          hasParticipantsArray: participantsToUse,
          hasParticipant: participantsToUse.length > 0,
          hasAddedAssistantAsParticipant: participantsToUse.length === 0,
        });
      }

      const existingConv = get().conversations[conversationId];
      // console.log('existingConv:', existingConv)
      if (!existingConv) {
        // First time seeing this ID, create a placeholder and fetch
        set((state) => ({
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...initialConversationState(conversationId, 'Loading...', participantsToUse),
              participants: participantsToUse,
              // Set isRecentlyAdded to false when user clicks on the conversation
              isRecentlyAdded: false,
            },
          },
        }));
        await get().fetchAndSetConversationMessages(conversationId, config, participantsToUse);
      } else if (!existingConv?.hasFetchedInitialMessages && !existingConv?.messages?.length) {
        // Exists but never fetched (e.g. from sidebar click before data loaded for it)
        // console.log('existingConv not found', existingConv)
        // Update participants on existing conversation
        set((state) => ({
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...state.conversations[conversationId],
              participants: participantsToUse,
              // Ensure modelName is always set
              modelName: state.conversations[conversationId]?.modelName || useModelStore.getState().selectedModel?.id || 'auto',
              // Set isRecentlyAdded to false when user clicks on the conversation
              isRecentlyAdded: false,
            },
          },
        }));
        await get().fetchAndSetConversationMessages(conversationId, config, participantsToUse);
      } else {
        // Update participants on existing conversation
        set((state) => ({
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...state.conversations[conversationId],
              participants: participantsToUse,
              // Ensure modelName is always set
              modelName: state.conversations[conversationId]?.modelName || useModelStore.getState().selectedModel?.id || 'auto',
              // Set isRecentlyAdded to false when user clicks on the conversation
              isRecentlyAdded: false,
            },
          },
        }));
      }
    }
  },

  fetchAndSetGroupChatParticipants: async (conversationId: string, config: ConfigurationSettings, refetch?: boolean, loading?: boolean) => {

    
    if (!conversationId) {
      return;
    }

    set({ activeConversationId: conversationId });

    const p = get().hasGroupChatParticipantsArray;
    const existingParticipants = p[conversationId];
    const isGroupChat = get().conversations[conversationId]?.isGroupChat;
    console.log('get().conversations[conversationId]', get().conversations[conversationId], "existingParticipants", existingParticipants,"isGroupChat", isGroupChat)

 
    if (!refetch && get().conversations[conversationId] && get().conversations[conversationId].isGroupChat === false){
      if (existingParticipants && existingParticipants.length > 0) {
  
        set((state) => ({
          hasParticipantsArray: existingParticipants,
          hasGroupChatParticipantsArray: {
            ...get().hasGroupChatParticipantsArray,
            [conversationId]: existingParticipants,
          },
          hasParticipant: true,
          hasAddedAssistantAsParticipant: false,
          isLoadingParticipants: false,
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...(state.conversations[conversationId] || initialConversationState(conversationId)),
              participants: existingParticipants,
              // Ensure modelName is always set
              modelName: state.conversations[conversationId]?.modelName || useModelStore.getState().selectedModel?.id || 'auto',
            },
          },
        }));
  
        await get().setActiveConversation(conversationId, config, existingParticipants);
  
        return;
      }
    }else{

      const setConversationLoading = (value: boolean) => {
        set((state) => ({
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...(state.conversations[conversationId] || initialConversationState(conversationId)),
              isLoading: value,
            },
          },
        }));
      };
  
      try {
        if (loading) {
          setConversationLoading(true);
        }
  
        const groupChatParticipants = await getGroupChatParticipants(conversationId, config);
        const models = useModelStore
          .getState()
          .models
  
  
        if (groupChatParticipants && Object.keys(groupChatParticipants).length > 0) {
          const modelIds = models.map(model => model.id);
          const participantKeys = Object.keys(groupChatParticipants);
          const modelParticipantKeys = participantKeys.filter(key => modelIds.includes(key));
          const nonModelParticipants = participantKeys.filter(key => !modelIds.includes(key));
  
          // && modelParticipantKeys.length > 0
          if (nonModelParticipants.length > 1) {
            const usersDataResponse = await getUsers({
              page: 0,
              pageSize: 50,
              filter: {
                userIds: nonModelParticipants,
              },
            }, config);
  
            // console.log('usersDataResponse.data', usersDataResponse.data)
            set((state) => ({
              hasGroupChatParticipantsArray: {
                ...get().hasGroupChatParticipantsArray,
                [conversationId]: usersDataResponse.data,
              },
              hasParticipantsArray: usersDataResponse.data,
              hasParticipant: true,
              hasAddedAssistantAsParticipant: false,
              conversations: {
                ...state.conversations,
                [conversationId]: {
                  ...(state.conversations[conversationId] || initialConversationState(conversationId)),
                  participants: usersDataResponse.data,
                  // Ensure modelName is always set
                  modelName: modelParticipantKeys[0]
                },
              },
            }));
  
            await get().setActiveConversation(conversationId, config, usersDataResponse.data);
  
          } else {
            set((state) => ({
              hasGroupChatParticipantsArray: {
                ...get().hasGroupChatParticipantsArray,
                [conversationId]: [],
              },
              hasParticipantsArray: [],
              hasParticipant: false,
              hasAddedAssistantAsParticipant: true,
              conversations: {
                ...state.conversations,
                [conversationId]: {
                  ...(state.conversations[conversationId] || initialConversationState(conversationId)),
                  participants: [],
                  // Ensure modelName is always set
                  modelName: modelParticipantKeys[0]
                },
              },
            }));
  
            await get().setActiveConversation(conversationId, config, []);
          }
        }
      } catch (error) {
        console.log('error in', error)
        // Ensure the per-conversation loading flag doesn't remain stuck on errors.
        setConversationLoading(false);
  
        set({
          isNewChat: true,
          hasParticipant: false,
          hasParticipantsArray: [],
          hasAddedAssistantAsParticipant: false,
          hasGroupChatParticipantsArray: {
            ...get().hasGroupChatParticipantsArray,
            [conversationId]: [],
          },
          isLoadingParticipants: false,
          activeConversationId: null,
        });
      } finally {
        // If callers opted into showing a loading state here, always turn it off once we're done.
        if (loading) {
          setConversationLoading(false);
        }
      }

    }



  },

  fetchAndSetConversationMessages: async (conversationId, config: ConfigurationSettings, participants?: IamData[]) => {
    if (!conversationId) return;



    if (!participants || participants.length === 0 || participants.length <= 1) {
      get().setHasAddedAssistantAsParticipant(true);

      set((state) => ({

        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...(state.conversations[conversationId] || initialConversationState(conversationId)),
            isLoading: true, // Loading history
            error: null,
            participants: participants || [],
          },

        },
      }));
      try {
        const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
        const url = `${baseUrl}${config.paths.taskforce.getCollaborationMessages}`;

        // Also fetch conversation events so we can populate removedParticipants
        const [res, conversation] = await Promise.all([
          clients.post<{ data: any }>(url, config.blocksKey, JSON.stringify({
            conversationId: conversationId,
            pageSize: 100
          })),
          getConversationById(conversationId, config),
        ]);

        const allEvents: ConversationEvent[] = conversation?.events || [];
        const removedEvents = allEvents.filter(
          (event: any) => event.eventType === 'USER_REMOVED'
        );
        const removedIds = new Set<string>();
        removedEvents.forEach((event: any) => {
          if (event?.targetIds && Array.isArray(event.targetIds)) {
            event.targetIds.forEach((id: string) => removedIds.add(id));
          }
        });

        if (removedIds.size > 0) {
          const removedUsersRes = await get().fetchUsersData(Array.from(removedIds), config);
          const fetchedRemovedUsers = removedUsersRes?.data || [];

          set((state) => {
            const existing = state.conversations[conversationId]?.removedParticipants || [];
            const mergedRemovedParticipants = [
              ...existing,
              ...fetchedRemovedUsers.filter(
                (u) => !existing.some((e) => e.itemId === u.itemId)
              ),
            ];
            return {
              conversations: {
                ...state.conversations,
                [conversationId]: {
                  ...(state.conversations[conversationId] || initialConversationState(conversationId)),
                  removedParticipants: mergedRemovedParticipants,
                },
              },
            };
          });
        }


        const fetchedMessages: Message[] = [];
        const conversationTitle = 'Chat'; // Default title
        const allFileIds: string[] = [];
        const currentUser = useCommonStore.getState().user;
        const currentUserId = currentUser?.id;
        if (res && res.data && Array.isArray(res.data)) {
          const messages = res.data;

          // Sort messages by createdAt (oldest first)
          const sortedMessages = [...messages].sort((a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );

          // const assistantMessage = sortedMessages.filter((msg: any) => msg.type === 'assistant');
          // // console.log('assistantMessage', assistantMessage) 
          // if (assistantMessage?.length > 0 && assistantMessage[assistantMessage.length - 1].senderId) {
          //   modelName = assistantMessage[assistantMessage.length - 1].senderId;
          //   isThinkingModel = useModelStore
          //     .getState()
          //     .models.some((model) => model.id === modelName && model.isThinkingModel);
          // }


          // Ensure modelName is always set - use fallback if not found in messages

          // if (!modelName || modelName === '') {
          //   const selectedModel = useModelStore.getState().selectedModel;
          //   modelName = selectedModel?.id || 'auto';
          // }



          const messageIdToRecordIdMap = new Map<string, string>();
          const recordIdToMessageIndexMap = new Map<string, number>();

          for (const msg of sortedMessages) {

            const isUserMessage = msg.type === 'user'
            const isAssistantMessage = msg.type === 'assistant';

            const attachments: Attachment[] = [];
            const attachmentIds: string[] = [];

            if (msg.attachments && Array.isArray(msg.attachments) && msg.attachments.length > 0) {
              for (const attachment of msg.attachments) {
                if (typeof attachment === 'string') {
                  attachmentIds.push(attachment);
                } else if (attachment && typeof attachment === 'object') {
                  if (attachment.fileId) {
                    attachments.push({
                      fileId: attachment.fileId,
                      fileName: attachment.fileName || `file_${attachment.fileId}`,
                      extension: attachment.extension || 'unknown',
                      sender: isUserMessage ? MessageRole.USER : MessageRole.ASSISTANT,
                    });
                    allFileIds.push(attachment.fileId);
                  } else if (attachment.FileId) {
                    // Handle camelCase variant
                    attachments.push({
                      fileId: attachment.FileId,
                      fileName: attachment.FileName || `file_${attachment.FileId}`,
                      extension: attachment.Extension || attachment.extension || 'unknown',
                      sender: isUserMessage ? MessageRole.USER : MessageRole.ASSISTANT,
                    });
                    allFileIds.push(attachment.FileId);
                  }
                }
              }
            }

            if (msg.metadata?.files && Array.isArray(msg.metadata.files) && msg.metadata.files.length > 0) {
              msg.metadata.files.forEach((file: any) => {
                if (file.fileId || file.FileId) {
                  attachmentIds.push(file.fileId || file.FileId);
                }
              });
            }
            let sources: Source[] = [];
            let searchSources: SearchSource[] = [];
            const generatedImages: Attachment[] = [];
            let hasFileGeneratedResponse = false;
            let usedModelId: string = msg.senderId || '';

            if (isAssistantMessage && msg.metadata) {
              // Process sources
              if (msg.metadata.sources && Array.isArray(msg.metadata.sources) && msg.metadata.sources.length > 0) {
                sources = msg.metadata.sources.map((source: any) => {
                  const fileId = source.file_id || source.fileId || '';
                  // Try to get fileName from chunks first, then from source directly
                  let fileName = '';
                  if (source.chunks && Array.isArray(source.chunks) && source.chunks.length > 0) {
                    fileName = source.chunks[0].file_name || source.chunks[0].fileName || '';
                  }
                  if (!fileName) {
                    fileName = source.fileName || source.file_name || '';
                  }
                  return {
                    fileId: fileId,
                    fileName: fileName || `file_${fileId}`,
                    chunks: [],
                  };
                });
              }


              if (msg.metadata.searchSources && Array.isArray(msg.metadata.searchSources) && msg.metadata.searchSources.length > 0) {
                searchSources = msg.metadata.searchSources.map((source: any) => ({
                  pageContent: source.PageContent || source.pageContent || '',
                  metadata: {
                    title: source.Metadata?.Title || source.metadata?.title || '',
                    url: source.Metadata?.Url || source.metadata?.url || '',
                  },
                }));
              }


              if (msg.metadata.files && Array.isArray(msg.metadata.files) && msg.metadata.files.length > 0) {
                for (const file of msg.metadata.files) {
                  if (file.fileId || file.FileId) {
                    const fileId = file.fileId || file.FileId;
                    generatedImages.push({
                      fileId: fileId,
                      fileName: file.fileName || file.FileName || `file_${fileId}`,
                      extension: file.extension || file.Extension || 'unknown',
                      sender: MessageRole.ASSISTANT,
                    });
                    allFileIds.push(fileId);
                  }
                }
              }

              if(msg.metadata && msg.metadata.autoSelectedModel) {
                usedModelId = msg.metadata.autoSelectedModel;
              }

              hasFileGeneratedResponse = msg.metadata.hasFileGeneratedResponse || false;
            }

            let content = msg.content || '';
            let thinkContent = '';

            // const modelUsed = useModelStore.getState().models.find(model => model.id === usedModelId);
            // const isThinking = modelUsed ? modelUsed.isThinkingModel || modelUsed.hasDynamicThinking || modelUsed.isHarmonicModel : false;

            if (isAssistantMessage) {
              const parsedContent = parseThinkingContent(content);
              thinkContent = parsedContent.thinkContent;
              content = parsedContent.responseContent;
            }

            content = extractLastResponseContent(content);

            // Helper function to get parentMessage from parentIds
            const getParentMessage = (): { senderName: string; content: string; time: number } | undefined => {
              if (!msg.parentIds || !Array.isArray(msg.parentIds) || msg.parentIds.length === 0) {
                return undefined;
              }

              const parentId = msg.parentIds[0];
              const parentMsg = sortedMessages.find((m: any) => m.id === parentId);

              if (!parentMsg) return undefined;

              // Get sender name from participants, removedParticipants, or senderId
              let senderName = 'User';
              if (parentMsg.senderId) {
                // Check participants first
                if (participants && participants.length > 0) {
                  const user = participants.find(p => p.itemId === parentMsg.senderId);
                  if (user) {
                    senderName = `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`;
                  }
                }

                // Check removedParticipants if not found in participants
                if (senderName === 'User') {
                  const currentConv = get().conversations[conversationId];
                  const removedParticipants = currentConv?.removedParticipants || [];
                  const removedUser = removedParticipants.find(p => p.itemId === parentMsg.senderId);
                  if (removedUser) {
                    senderName = `${removedUser.firstName}${removedUser.lastName ? ` ${removedUser.lastName}` : ''}`;
                  }
                }

                // Fallback to current user if senderId matches
                if (senderName === 'User' && currentUserId === parentMsg.senderId) {
                  senderName = `${currentUser?.firstName || ''}${currentUser?.lastName ? ` ${currentUser.lastName}` : ''}`.trim() || 'User';
                }
              }

              return {
                senderName: senderName,
                content: parentMsg.content || '',
                time: new Date(parentMsg.createdAt).getTime() || Date.now(),
              };
            };

            const parentMessage = getParentMessage();

            if (isUserMessage) {
              const userGeneration = {
                id: msg.id,
                content: content,
                createdDate: new Date(msg.createdAt).getTime() || Date.now(),
                attachments: attachments,
                ...(msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0 && { parentIds: msg.parentIds }),
                ...(parentMessage && { parentMessage }),
              };

              // Check if this message has an originalMessageId (edited message)
              if (msg.originalMessageId) {
                // Find the original user message to add this as a new generation
                const originalMessageIndex = fetchedMessages.findIndex(
                  (m) => m.role === MessageRole.USER && 
                    (m.recordId === msg.originalMessageId || 
                     m.generations.some(g => g.id === msg.originalMessageId))
                );

                if (originalMessageIndex !== -1) {
                  // Add as a new generation to the original message
                  fetchedMessages[originalMessageIndex].generations.push(userGeneration);
                  // Update activeGenerationIndex to show the latest generation
                  fetchedMessages[originalMessageIndex].activeGenerationIndex = 
                    fetchedMessages[originalMessageIndex].generations.length - 1;
                  // Store mapping using the original message's recordId
                  messageIdToRecordIdMap.set(msg.id, fetchedMessages[originalMessageIndex].recordId);
                } else {
                  // Original message not found yet, create new message
                  // This shouldn't happen if messages are sorted by createdAt correctly
                  fetchedMessages.push({
                    recordId: msg.id,
                    role: MessageRole.USER,
                    senderId: msg.senderId,
                    generations: [userGeneration],
                  });
                  messageIdToRecordIdMap.set(msg.id, msg.id);
                  recordIdToMessageIndexMap.set(msg.id, fetchedMessages.length - 1);
                }
              } else {
                // No originalMessageId - create new user message
                fetchedMessages.push({
                  recordId: msg.id,
                  role: MessageRole.USER,
                  senderId: msg.senderId,
                  generations: [userGeneration],
                });
                // Store mapping for parent lookup
                messageIdToRecordIdMap.set(msg.id, msg.id);
                recordIdToMessageIndexMap.set(msg.id, fetchedMessages.length - 1);
              }
            } else if (isAssistantMessage) {
              // Extract feedback from reactions

              const feedback = extractFeedbackFromReactions(msg.reactions, currentUserId);

              // Find parentUserGenerationId
              let parentUserGenerationId: string | undefined;
              const hasParentIds = msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0;

              if (hasParentIds) {
                // First check if parentIds points to a user message generation
                for (const parentId of msg.parentIds) {
                  const parentUserMessage = fetchedMessages.find(m =>
                    m.role === MessageRole.USER &&
                    m.generations.some(g => g.id === parentId)
                  );
                  if (parentUserMessage) {
                    parentUserGenerationId = parentId;
                    break;
                  }
                }

                // If not found in user messages, check if parentIds points to an assistant message
                // and inherit parentUserGenerationId from it (regeneration case)
                if (!parentUserGenerationId) {
                  for (const parentId of msg.parentIds) {
                    const parentAssistantMessage = fetchedMessages.find(m =>
                      m.role === MessageRole.ASSISTANT &&
                      m.generations.some(g => g.id === parentId)
                    );
                    if (parentAssistantMessage) {
                      // Find the generation and inherit its parentUserGenerationId
                      const parentGeneration = parentAssistantMessage.generations.find(g => g.id === parentId);
                      if (parentGeneration?.parentUserGenerationId) {
                        parentUserGenerationId = parentGeneration.parentUserGenerationId;
                      }
                      break; // Stop here, don't fall back to relatedUserMessageIds for regeneration
                    }
                  }
                }
              } else {
                // No parentIds - use relatedUserMessageIds from metadata
                if (msg.metadata?.relatedUserMessageIds?.[0]) {
                  const relatedUserId = msg.metadata.relatedUserMessageIds[0];
                  // Verify it exists in user messages
                  const relatedUserMessage = fetchedMessages.find(m =>
                    m.role === MessageRole.USER &&
                    m.generations.some(g => g.id === relatedUserId)
                  );
                  if (relatedUserMessage) {
                    parentUserGenerationId = relatedUserId;
                  }
                }
              }

              const generation: MessageGeneration = {
                id: msg.id,
                content: content,
                createdDate: new Date(msg.createdAt).getTime() || Date.now(),
                attachments: attachments,
                
                isThisGenerationOnGoing: false,
                HasFileGeneratedResponse: hasFileGeneratedResponse,
                generatedImages: generatedImages.length > 0 ? generatedImages : undefined,
                sources: sources.length > 0 ? sources : undefined,
                searchSources: searchSources.length > 0 ? searchSources : undefined,
                feedback: feedback,
                ...(msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0 && { parentIds: msg.parentIds }),
                ...(parentMessage && { parentMessage }),
                usedModelId: usedModelId,
                ...(parentUserGenerationId && { parentUserGenerationId }),
              };

              if (thinkContent) {
                generation.thinkContent = thinkContent;
              }


              // const targetRecordId = messageRecordId;
              const userRecordIdForAssistant = msg.metadata?.relatedUserMessageIds?.[0];

              // Check if parentIds point to an assistant message (for grouping generations)
              let parentAssistantRecordId: string | null = null;
              if (msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0) {
                for (const parentId of msg.parentIds) {
                  // First check if parentId is a message ID we've already mapped
                  if (messageIdToRecordIdMap.has(parentId)) {
                    const mappedRecordId = messageIdToRecordIdMap.get(parentId)!;
                    // Check if the parent is an assistant message
                    const parentMessage = fetchedMessages.find(m => m.recordId === mappedRecordId);
                    if (parentMessage && parentMessage.role === MessageRole.ASSISTANT) {
                      parentAssistantRecordId = mappedRecordId;
                      break;
                    }
                  } else {
                    // Check if parentId matches any generation ID in existing assistant messages
                    const parentMessage = fetchedMessages.find(m =>
                      m.role === MessageRole.ASSISTANT &&
                      m.generations.some(g => g.id === parentId)
                    );
                    if (parentMessage) {
                      parentAssistantRecordId = parentMessage.recordId;
                      // Store the mapping for future lookups
                      messageIdToRecordIdMap.set(parentId, parentMessage.recordId);
                      break;
                    }
                  }
                }
              }

              // If parentIds point to an assistant message (regeneration), add as generation to that assistant
              if (parentAssistantRecordId) {
                const parentAssistantIndex = fetchedMessages.findIndex(
                  (m) => m.recordId === parentAssistantRecordId
                );
                if (parentAssistantIndex !== -1) {
                  fetchedMessages[parentAssistantIndex].generations.push(generation);
                  fetchedMessages[parentAssistantIndex].activeGenerationIndex = 
                    fetchedMessages[parentAssistantIndex].generations.length - 1;
                  // Store mapping using the parent's recordId so future messages can find it
                  messageIdToRecordIdMap.set(msg.id, parentAssistantRecordId);
                } else {
                  // Parent not found yet, create new message (shouldn't happen if sorted correctly)
                  fetchedMessages.push({
                    role: MessageRole.ASSISTANT,
                    recordId: generation.id,
                    userRecordId: userRecordIdForAssistant,
                    activeGenerationIndex: 0,
                    generations: [generation],
                    senderId: msg.senderId,
                    ...(msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0 && { parentIds: msg.parentIds }),
                  });
                  recordIdToMessageIndexMap.set(generation.id, fetchedMessages.length - 1);
                  messageIdToRecordIdMap.set(msg.id, generation.id);
                }
              } else {
                // Check if relatedUserMessageIds points to a user generation that's part of an edited message
                // If so, find the assistant that responds to that user Message and add as generation
                let existingAssistantForUserMessage: Message | undefined;
                
                if (userRecordIdForAssistant) {
                  // Find the user Message that contains this generation
                  const userMessageWithGeneration = fetchedMessages.find(m =>
                    m.role === MessageRole.USER &&
                    m.generations.some(g => g.id === userRecordIdForAssistant)
                  );
                  
                  if (userMessageWithGeneration) {
                    // Find the assistant that responds to this user Message (by recordId)
                    existingAssistantForUserMessage = fetchedMessages.find(m =>
                      m.role === MessageRole.ASSISTANT &&
                      m.userRecordId && 
                      userMessageWithGeneration.generations.some(g => g.id === m.userRecordId)
                    );
                  }
                }

                if (existingAssistantForUserMessage) {
                  // Add generation to existing assistant message (edit scenario)
                  const existingAssistantIndex = fetchedMessages.findIndex(
                    m => m.recordId === existingAssistantForUserMessage!.recordId
                  );
                  if (existingAssistantIndex !== -1) {
                    fetchedMessages[existingAssistantIndex].generations.push(generation);
                    fetchedMessages[existingAssistantIndex].activeGenerationIndex = 
                      fetchedMessages[existingAssistantIndex].generations.length - 1;
                    messageIdToRecordIdMap.set(msg.id, existingAssistantForUserMessage.recordId);
                  }
                } else {
                  // Create new message entry
                  fetchedMessages.push({
                    role: MessageRole.ASSISTANT,
                    recordId: generation.id,
                    userRecordId: userRecordIdForAssistant,
                    activeGenerationIndex: 0,
                    generations: [generation],
                    senderId: msg.senderId,
                    ...(msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0 && { parentIds: msg.parentIds }),
                  });
                  recordIdToMessageIndexMap.set(generation.id, fetchedMessages.length - 1);
                  messageIdToRecordIdMap.set(msg.id, generation.id);
                }
              }

            }
          }
        }
        set((state) => ({
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...(state.conversations[conversationId] || initialConversationState(conversationId)),
              messages: fetchedMessages,
              title: conversationTitle,
              isLoading: false, // Done loading history
              hasFetchedInitialMessages: true,
              error: null,
              // modelName: modelName,
              allFileIds: allFileIds,
              isGroupChat: false,
              participants: participants || [],
              customGptId: conversation?.customGptId,
            },
          },
        }));
      } catch (error) {
        console.error('Failed to fetch conversation:', error);
        // Ensure modelName is set even in error case
        // const selectedModel = useModelStore.getState().selectedModel;
        // const fallbackModelName = selectedModel?.id || 'auto';

        set((state) => ({
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...(state.conversations[conversationId] || initialConversationState(conversationId)),
              isLoading: false,
              hasFetchedInitialMessages: false, // So it can be retried
              error: 'Failed to load messages.',
              // modelName: state.conversations[conversationId]?.modelName || fallbackModelName,
              participants: participants || [],
            },
          },
        }));
      }

    } else {
      // console.log('participants in fetchAndSetConversationMessages found', participants)

      get().setHasAddedAssistantAsParticipant(false);
      set((state) => ({
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...(state.conversations[conversationId] || initialConversationState(conversationId)),
            isLoading: true,
            error: null,
            participants: participants || [],
            // Ensure modelName is always set
            // modelName: state.conversations[conversationId]?.modelName || useModelStore.getState().selectedModel?.id || 'auto',
          },
        },
      }));

      try {
        const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
        const url = `${baseUrl}${config.paths.taskforce.getCollaborationMessages}`;

        const [res, events] = await Promise.all([
          clients.post<{ data: any }>(url, config.blocksKey, JSON.stringify({
            "conversationId": conversationId,
            "pageSize": 100
          })),
          getConversationById(conversationId, config)
        ]);
        const allEvents: ConversationEvent[] = events.events || [];
        const systemMessages: Message[] = [];
        let allRemovedParticipants: IamData[] = [];

        if (Array.isArray(allEvents) && allEvents.length > 0) {
          const userAddedEvent = allEvents.filter((event: any) => event.eventType === 'USER_ADDED');
          const userAddedByTokenEvent = allEvents.filter((event: any) => event.eventType === 'USER_ADDED_BY_TOKEN');
          const conversationTitleUpdatedEvent = allEvents.filter((event: any) => event.eventType === 'CONVERSATION_TITLE_UPDATED');
          const conversationCreatedEvent = allEvents.filter((event: any) => event.eventType === 'CONVERSATION_CREATED');

          const removedParticipantEvent = allEvents.filter((event: any) => event.eventType === 'USER_REMOVED');
          const participantIdsToFetch = new Set<string>();
          const existingParticipantIds = new Set(participants.map(p => p.itemId));

          if (conversationCreatedEvent.length > 0) {
            const conversationCreatedMessages = conversationCreatedEvent.map((event: any) => {
              const createdBy = participants.find((participant: any) => participant.itemId === event.actorId) || 
                (useCommonStore.getState().user?.id === event.actorId ? useCommonStore.getState().user : null);
              const messageContent = `${createdBy?.firstName || 'Someone'} created the conversation`;
              const generationId = uuidv4();
              return {
                recordId: generationId,
                role: MessageRole.CONVERSATION_CREATED,
                generations: [
                  {
                    id: generationId,
                    content: messageContent,
                    createdDate: new Date(event.createdAt).getTime(),
                  },
                ],
              };
            });
            systemMessages.push(...conversationCreatedMessages);
          }
          userAddedEvent.forEach((event: any) => {
            if (event.targetIds && Array.isArray(event.targetIds)) {
              event.targetIds.forEach((id: string) => {
                if (!existingParticipantIds.has(id)) {
                  participantIdsToFetch.add(id);
                }
              });
            }
          });
          userAddedByTokenEvent.forEach((event: any) => {
            if (event.targetIds && Array.isArray(event.targetIds)) {
              event.targetIds.forEach((id: string) => {
                if (!existingParticipantIds.has(id)) {
                  participantIdsToFetch.add(id);
                }
              });
            }
          });

          removedParticipantEvent.forEach((event: any) => {
            if (event.targetIds && Array.isArray(event.targetIds)) {
              event.targetIds.forEach((id: string) => participantIdsToFetch.add(id));
            }
          });

          const fetchedParticipantsMap = new Map<string, IamData>();
          if (participantIdsToFetch.size > 0) {
            const allFetchedParticipantsResponse = await get().fetchUsersData(Array.from(participantIdsToFetch), config);
            const allFetchedParticipants = allFetchedParticipantsResponse?.data || [];
            allFetchedParticipants.forEach((p: IamData) => {
              fetchedParticipantsMap.set(p.itemId, p);
            });
          }

          const addedParticipantIds = new Set<string>();
          userAddedEvent.forEach((event: any) => {
            if (event.targetIds && Array.isArray(event.targetIds)) {
              event.targetIds.forEach((id: string) => {
                if (!existingParticipantIds.has(id)) {
                  addedParticipantIds.add(id);
                }
              });
            }
          });

          userAddedByTokenEvent.forEach((event: any) => {
            if (event.targetIds && Array.isArray(event.targetIds)) {
              event.targetIds.forEach((id: string) => {
                if (!existingParticipantIds.has(id)) {
                  addedParticipantIds.add(id);
                }
              });
            }
          });

          const removedParticipantIds = new Set<string>();
          removedParticipantEvent.forEach((event: any) => {
            if (event.targetIds && Array.isArray(event.targetIds)) {
              event.targetIds.forEach((id: string) => removedParticipantIds.add(id));
            }
          });

          allRemovedParticipants = Array.from(removedParticipantIds)
            .map(id => fetchedParticipantsMap.get(id))
            .filter((p): p is IamData => p !== undefined);

          const addParticipantMessages = userAddedEvent.map((event: any) => {
            const addedBy = participants.find((participant: any) => participant.itemId === event.actorId);

            // Get participant data from existing participants or fetched data
            let participantData: any[] = participants.filter((participant: any) =>
              event.targetIds?.includes(participant.itemId)
            );

            // If not found in existing participants, get from fetched data
            if (participantData.length === 0) {
              participantData = event.targetIds
                ?.map((id: string) => fetchedParticipantsMap.get(id))
                .filter((p: IamData | undefined): p is IamData => p !== undefined) || [];
            }

            const addParticipantNames = participantData
              .map((participant: any) => participant.firstName)
              .filter((name: string) => name)
              .join(', ');

            const messageContent = `${addedBy?.firstName || 'Someone'} added ${addParticipantNames || 'users'}`;
            const generationId = uuidv4();
            return {
              recordId: generationId,
              role: MessageRole.ADD_PARTICIPANT,
              generations: [
                {
                  id: generationId,
                  content: messageContent,
                  createdDate: new Date(event.createdAt).getTime(),
                },
              ],
            };
          });
          systemMessages.push(...addParticipantMessages);

          // Handle USER_ADDED_BY_TOKEN events - show "joined" messages
          if (userAddedByTokenEvent.length > 0) {
            const joinedByTokenMessages: Message[] = [];
            
            userAddedByTokenEvent.forEach((event: any) => {
              const eventDate = new Date(event.createdAt);
              
              // Get participant data from existing participants or fetched data
              let participantData: any[] = participants.filter((participant: any) =>
                event.targetIds?.includes(participant.itemId)
              );

              // If not found in existing participants, get from fetched data
              if (participantData.length === 0) {
                participantData = event.targetIds
                  ?.map((id: string) => fetchedParticipantsMap.get(id))
                  .filter((p: IamData | undefined): p is IamData => p !== undefined) || [];
              }

              const joinedParticipantNames = participantData
                .map((participant: any) => participant.firstName)
                .filter((name: string) => name)
                .join(', ');

              // Create time separator message
              const timeSeparatorId = uuidv4();
              const timeSeparatorMessage: Message = {
                recordId: timeSeparatorId,
                role: MessageRole.CONVERSATION_TITLE_UPDATED, // Using this role for time separator
                generations: [
                  {
                    id: timeSeparatorId,
                    content: formatDateWithTime(eventDate),
                    createdDate: eventDate.getTime(),
                  },
                ],
              };
              joinedByTokenMessages.push(timeSeparatorMessage);

              // Create "joined" message
              const messageContent = joinedParticipantNames 
                ? `${joinedParticipantNames} joined the chat`
                : 'Someone joined the chat';
              
              const generationId = uuidv4();
              const joinedMessage: Message = {
                recordId: generationId,
                role: MessageRole.ADD_PARTICIPANT,
                generations: [
                  {
                    id: generationId,
                    content: messageContent,
                    createdDate: eventDate.getTime(),
                  },
                ],
              };
              joinedByTokenMessages.push(joinedMessage);
            });
            
            systemMessages.push(...joinedByTokenMessages);
          }


          if (conversationTitleUpdatedEvent.length > 0) {
            const titleUpdatedMessages = conversationTitleUpdatedEvent.map((event: any) => {
              const updatedBy = participants.find((participant: any) => participant.itemId === event.actorId);
              const messageContent = `${updatedBy?.firstName || 'Someone'} updated the conversation title`;
              const generationId = uuidv4();
              return {
                recordId: generationId,
                role: MessageRole.CONVERSATION_TITLE_UPDATED,
                generations: [
                  {
                    id: generationId,
                    content: messageContent,
                    createdDate: new Date(event.createdAt).getTime(),
                  },
                ],
              };
            });
            // console.log('systemMessage in conversation title updated', titleUpdatedMessages)
            systemMessages.push(...titleUpdatedMessages);
          }

          if (removedParticipantEvent.length > 0) {
            const removedParticipantMessages = removedParticipantEvent.map((event: any) => {
              const removedBy = participants.find((participant: any) => participant.itemId === event.actorId) || allRemovedParticipants.find((participant: any) => participant.itemId === event.actorId);

              // Check if the actor is removing themselves
              const isSelfRemoval = event.targetIds?.includes(event.actorId);

              // Get removed participants for this specific event
              const eventRemovedParticipants = allRemovedParticipants.filter((p: IamData) =>
                event.targetIds?.includes(p.itemId)
              );

     
              let messageContent: string;
              if (isSelfRemoval) {
                // If actor removed themselves, show "left the conversation"
                const leavingUser = eventRemovedParticipants.find(
                  (participant: any) => participant.itemId === event.actorId
                ) || removedBy;
                const leavingUserName = leavingUser?.firstName || 'Someone';
                messageContent = `${leavingUserName} left the conversation`;
              } else {
                // Otherwise, show "removed {users}"
                const removedParticipantNames = eventRemovedParticipants
                  .map((participant: any) => participant.firstName)
                  .filter((name: string) => name)
                  .join(', ') || 'users';
                messageContent = `${removedBy?.firstName || 'Someone'} removed ${removedParticipantNames}`;
              }

              const generationId = uuidv4();
              return {
                recordId: generationId,
                role: MessageRole.USER_REMOVED,
                generations: [
                  {
                    id: generationId,
                    content: messageContent,
                    createdDate: new Date(event.createdAt).getTime(),
                  },
                ],
              };
            });
            systemMessages.push(...removedParticipantMessages);
          }
        }

        const fetchedMessages: Message[] = [];
        const conversationTitle = 'Chat';
        const currentUser = useCommonStore.getState().user;
        const currentUserId = currentUser?.id;

        if (res && res.data && Array.isArray(res.data)) {
          const messages = res.data;

          // Sort messages by createdAt (oldest first)
          const sortedMessages = [...messages].sort((a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );

          // console.log('sortedMessages in fetchCollaborationMessages', sortedMessages)
          const addUserToAttachments = (attachments: Attachment[], sender: MessageRole): FileMetadata[] => {
            if (!attachments || attachments.length === 0) {
              return [];
            }

            return attachments.map((attachment) => {
              return {
                fileId: attachment.fileId,
                fileName: attachment.fileName,
                extension: attachment.extension,
                sender: sender,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                size: 0,
              };
            });
          };

          // Get participants from conversation state
          const conv = get().conversations[conversationId];
          const convParticipants = conv?.participants || [];

          // Get selected model to add as EagleGpt user
          const selectedModel = useModelStore.getState().selectedModel;

          // Create a map of user IDs to user data for quick lookup
          // Use participants parameter first, then fall back to conversation participants
          const userMap = new Map<string, IamData>();
          const allParticipants = participants && participants.length > 0 ? participants : convParticipants;
          allParticipants.forEach((participant) => {
            userMap.set(participant.itemId, participant);
          });

          // Add removed participants to userMap for lookup
          const convRemovedParticipants = conv?.removedParticipants || [];
          convRemovedParticipants.forEach((participant) => {
            // Only add if not already in map (participants take priority)
            if (!userMap.has(participant.itemId)) {
              userMap.set(participant.itemId, participant);
            }
          });

          // Add selected model (EagleGpt) to userMap if it exists
          if (selectedModel) {
            const modelAsUser: IamData = {
              itemId: selectedModel.id,
              createdDate: '',
              lastUpdatedDate: '',
              lastLoggedInTime: '',
              language: '',
              salutation: '',
              firstName: 'EagleGpt',
              lastName: null,
              email: selectedModel.id,
              userName: selectedModel.id,
              phoneNumber: null,
              roles: [],
              permissions: [],
              active: true,
              isVarified: false,
              profileImageUrl: null,
              mfaEnabled: false,
              organizations: [],
              plan: { itemId: '', name: '' },
              planItem: { itemId: '', title: null },
              groups: [],
            };
            userMap.set(selectedModel.id, modelAsUser);
          }

          const convertBackendMentionsToMentionObjects = (
            mentionUserIds: string[],
            content: string
          ): Mention[] => {
            if (!mentionUserIds || mentionUserIds.length === 0 || !content) {
              return [];
            }

            const mentions: Mention[] = [];

            // For each mentioned user ID, find their name in the content
            mentionUserIds.forEach((userId) => {
              const user = userMap.get(userId);
              if (!user) {
                // If user still not found, skip it (we already tried to fetch)
                console.warn('User not found for mention:', userId);
                return;
              }

              const userName = `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`;

              // Find all occurrences of the user name in the content
              let searchStart = 0;
              let index = content.indexOf(userName, searchStart);
              while (index !== -1) {

                // Check if this is a word boundary (not part of another word)
                const beforeChar = index > 0 ? content[index - 1] : ' ';
                const afterChar = index + userName.length < content.length
                  ? content[index + userName.length]
                  : ' ';

                // Only consider it a mention if it's at word boundaries
                if ((beforeChar === ' ' || beforeChar === '\n' || index === 0) &&
                  (afterChar === ' ' || afterChar === '\n' || index + userName.length === content.length)) {
                  mentions.push({
                    userId: userId,
                    trigger: `@${userName}`,
                    start: index,
                    end: index + userName.length,
                    user: user,
                  });
                  // Move past this occurrence
                  searchStart = index + userName.length;
                } else {
                  // Move past this occurrence even if not a match
                  searchStart = index + 1;
                }

                // Find next occurrence
                index = content.indexOf(userName, searchStart);
              }
            });

            // Sort mentions by start position and remove duplicates
            return mentions
              .sort((a, b) => a.start - b.start)
              .filter((mention, index, arr) => {
                // Remove duplicates (same userId and position)
                return index === 0 ||
                  mention.userId !== arr[index - 1].userId ||
                  mention.start !== arr[index - 1].start;
              });
          };

          // Create mapping to track message IDs to recordIds (similar to non-group chat)
          const messageIdToRecordIdMap = new Map<string, string>();

          // console.log('sortedMessages in fetchCollaborationMessages', sortedMessages)
          for (const msg of sortedMessages) {
            const isUserMessage = msg.type === 'user' && msg.senderId === currentUserId;
            const isUserAssistant = msg.type === 'user' && msg.senderId !== currentUserId;
            const isAssistantMessage = msg.type === 'assistant';

            // const attachments = await fetchAttachmentMetadata(msg.attachments || [], isUserMessage ? MessageRole.USER : MessageRole.ASSISTANT);

            const messageRecordId = msg.id;

            // Helper function to get parentMessage from parentIds
            const getParentMessageData = (): { senderName: string; content: string; time: number } | undefined => {
              if (!msg.parentIds || !Array.isArray(msg.parentIds) || msg.parentIds.length === 0) {
                return undefined;
              }

              // Find the first parent message in sortedMessages
              const parentId = msg.parentIds[0];
              const parentMsg = sortedMessages.find((m: any) => m.id === parentId);

              if (!parentMsg) return undefined;

              // Get sender name from userMap
              let senderName = 'User';
              if (parentMsg.senderId) {
                const user = userMap.get(parentMsg.senderId);
                if (user) {
                  senderName = `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`;
                }
              }

              return {
                senderName: senderName,
                content: parentMsg.content || '',
                time: new Date(parentMsg.createdAt).getTime() || Date.now(),
              };
            };

            const parentMessageData = getParentMessageData();

            if (isUserMessage) {
              // Convert backend mentions (array of user IDs) to Mention objects
              const mentions = msg.mentions && Array.isArray(msg.mentions) && msg.mentions.length > 0
                ? convertBackendMentionsToMentionObjects(msg.mentions, msg.content || '')
                : undefined;

              fetchedMessages.push({
                recordId: messageRecordId,
                role: MessageRole.USER_CHAT,
                senderId: msg.senderId,
                generations: [{
                  id: messageRecordId,
                  content: msg.content || '',
                  createdDate: new Date(msg.createdAt).getTime() || Date.now(),
                  attachments: addUserToAttachments(msg.attachments, MessageRole.USER),
                  ...(msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0 && { parentIds: msg.parentIds }),
                  ...(parentMessageData && { parentMessage: parentMessageData }),
                  ...(mentions && mentions.length > 0 && { mentions: mentions }),
                }],
              });
              // Store mapping for parent lookup
              messageIdToRecordIdMap.set(msg.id, messageRecordId);
            } else if (isUserAssistant) {
              // Extract feedback from reactions
              const mentions = msg.mentions && Array.isArray(msg.mentions) && msg.mentions.length > 0
                ? convertBackendMentionsToMentionObjects(msg.mentions, msg.content || '')
                : undefined;
              const feedback = extractFeedbackFromReactions(msg.reactions, currentUserId);

              fetchedMessages.push({
                recordId: messageRecordId,
                role: MessageRole.ASSISTANT_CHAT,
                senderId: msg.senderId,
                generations: [{
                  id: messageRecordId,
                  content: msg.content || '',
                  createdDate: new Date(msg.createdAt).getTime() || Date.now(),
                  attachments: addUserToAttachments(msg.attachments, MessageRole.ASSISTANT),
                  feedback: feedback,
                  ...(msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0 && { parentIds: msg.parentIds }),
                  ...(parentMessageData && { parentMessage: parentMessageData }),
                  ...(mentions && mentions.length > 0 && { mentions: mentions }),
                }],
                toolsStatus: msg.toolsStatus,

              });
              // Store mapping for parent lookup
              messageIdToRecordIdMap.set(msg.id, messageRecordId);
            } else if (isAssistantMessage) {
              const feedback = extractFeedbackFromReactions(msg.reactions, currentUserId);

              // Process metadata files for generated images
              const generatedImages: Attachment[] = [];
              let hasFileGeneratedResponse = false;
              let searchSources = [];
              let usedModelId: string = msg.senderId || '';

              if (msg.metadata) {
                if (msg.metadata.files && Array.isArray(msg.metadata.files) && msg.metadata.files.length > 0) {
                  for (const file of msg.metadata.files) {
                    if (file.fileId || file.FileId) {
                      const fileId = file.fileId || file.FileId;
                      generatedImages.push({
                        fileId: fileId,
                        fileName: file.fileName || file.FileName || `file_${fileId}`,
                        extension: file.extension || file.Extension || 'unknown',
                        sender: MessageRole.ASSISTANT,
                      });
                    }
                  }
                }
                hasFileGeneratedResponse = msg.metadata.hasFileGeneratedResponse || false;

                if (msg.metadata.searchSources && Array.isArray(msg.metadata.searchSources) && msg.metadata.searchSources.length > 0) {
                searchSources = msg.metadata.searchSources.map((source: any) => ({
                  pageContent: source.PageContent || source.pageContent || '',
                  metadata: {
                    title: source.Metadata?.Title || source.metadata?.title || '',
                    url: source.Metadata?.Url || source.metadata?.url || '',
                  },
                }));

                if(msg.metadata && msg.metadata.autoSelectedModel) {
                  usedModelId = msg.metadata.autoSelectedModel;
                }
              }
              }



              // Check if parentIds point to an assistant message (for grouping generations)
              let parentAssistantRecordId: string | null = null;
              if (msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0) {
                for (const parentId of msg.parentIds) {
                  // First check if parentId is a message ID we've already mapped
                  if (messageIdToRecordIdMap.has(parentId)) {
                    const mappedRecordId = messageIdToRecordIdMap.get(parentId)!;
                    // Check if the parent is an assistant message
                    const parentMessage = fetchedMessages.find(m => m.recordId === mappedRecordId);
                    if (parentMessage && parentMessage.role === MessageRole.ASSISTANT) {
                      parentAssistantRecordId = mappedRecordId;
                      break;
                    }
                  } else {
                    // Check if parentId matches any generation ID in existing assistant messages
                    const parentMessage = fetchedMessages.find(m =>
                      m.role === MessageRole.ASSISTANT &&
                      m.generations.some(g => g.id === parentId)
                    );
                    if (parentMessage) {
                      parentAssistantRecordId = parentMessage.recordId;
                      // Store the mapping for future lookups
                      messageIdToRecordIdMap.set(parentId, parentMessage.recordId);
                      break;
                    }
                  }
                }
              }


              const userRecordIdForAssistant = msg.metadata?.relatedUserMessageIds?.[0];

              // Extract thinkContent for thinking models (same logic as regular chat)
              let content = msg.content || '';
              let thinkContent = '';


              // const modelUsed = useModelStore.getState().models.find(model => model.id === usedModelId);
              // const isThinkingModel = modelUsed?.isThinkingModel && !modelUsed?.isHarmonicModel && !modelUsed?.hasDynamicThinking;

              if (isAssistantMessage) {
                const parsedContent = parseThinkingContent(content);
                thinkContent = parsedContent.thinkContent;
                content = parsedContent.responseContent;
              }

              const newGeneration = {
                id: messageRecordId,
                content: content,
                createdDate: new Date(msg.createdAt).getTime() || Date.now(),
                attachments: addUserToAttachments(msg.attachments, MessageRole.ASSISTANT),
                isThisGenerationOnGoing: false,
                HasFileGeneratedResponse: hasFileGeneratedResponse,
                generatedImages: generatedImages.length > 0 ? generatedImages : undefined,
                feedback: feedback,
                ...(thinkContent && { thinkContent: thinkContent }),
                ...(msg.parentIds && Array.isArray(msg.parentIds) && msg.parentIds.length > 0 && { parentIds: msg.parentIds }),
                ...(parentMessageData && { parentMessage: parentMessageData }),
                ...(searchSources && { searchSources: searchSources }),
                usedModelId: usedModelId,
              };

              // If parentIds point to an assistant message, add as generation to that assistant
              if (parentAssistantRecordId) {
                const parentAssistantIndex = fetchedMessages.findIndex(
                  (m) => m.recordId === parentAssistantRecordId
                );
                if (parentAssistantIndex !== -1) {
                  // Add generation to existing assistant message
                  fetchedMessages[parentAssistantIndex].generations.push(newGeneration);
                  // Store mapping using the parent's recordId so future messages can find it
                  messageIdToRecordIdMap.set(msg.id, parentAssistantRecordId);
                } else {
                  // Parent not found yet, create new message (shouldn't happen if sorted correctly)
                  fetchedMessages.push({
                    recordId: newGeneration.id,
                    ...(userRecordIdForAssistant && { userRecordId: userRecordIdForAssistant }),
                    // ...(userMessageData && { userMessage: userMessageData }),
                    role: MessageRole.ASSISTANT,
                    senderId: msg.senderId,
                    generations: [newGeneration],
                    toolsStatus: msg.toolsStatus,

                  });
                  messageIdToRecordIdMap.set(msg.id, newGeneration.id);
                }
              } else {
                // No parent assistant message, create new assistant message
                fetchedMessages.push({
                  recordId: newGeneration.id,
                  ...(userRecordIdForAssistant && { userRecordId: userRecordIdForAssistant }),
                  // ...(userMessageData && { userMessage: userMessageData }),
                  role: MessageRole.ASSISTANT,
                  senderId: msg.senderId,
                  generations: [newGeneration],
                  toolsStatus: msg.toolsStatus,
                });
                // Store mapping for parent lookup
                messageIdToRecordIdMap.set(msg.id, newGeneration.id);
              }
            }
          }
        }

        // Merge systemMessages with fetchedMessages and sort by createdDate
        const allMessages = [...fetchedMessages, ...systemMessages].sort((a, b) => {
          const aDate = a.generations[0]?.createdDate || 0;
          const bDate = b.generations[0]?.createdDate || 0;
          return aDate - bDate;
        });

        set((state) => {
          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...(state.conversations[conversationId] || initialConversationState(conversationId)),
                messages: allMessages,
                title: conversationTitle,
                isLoading: false,
                hasFetchedInitialMessages: true,
                error: null,
                isGroupChat: true,
                participants: participants || [],
                removedParticipants: allRemovedParticipants,
              },
            },
          };
        });
      } catch (error) {
        console.error('Failed to fetch collaboration messages:', error);

        set((state) => ({
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...(state.conversations[conversationId] || initialConversationState(conversationId)),
              isLoading: false,
              hasFetchedInitialMessages: false,
              error: 'Failed to load messages.',
              // modelName: state.conversations[conversationId]?.modelName || fallbackModelName,
              participants: participants || [],
            },
          },
        }));
      }
    }

  },

  handleGroupChatNotification: async (notification: any, config?: ConfigurationSettings, type?: string, userIds?: { invitedBy: string, invitedTo: string[] }) => {
    if (!config) return;

    if (type === "USER_MESSAGE") {
      try {
        if (!notification || !notification.response) return;
        const notificationData = notification.response;
        const { conversationId, message } = notificationData;
        if (!conversationId) {
          return;
        }
        const currentUser = useCommonStore.getState().user;
        const currentUserId = currentUser?.id;

        if (!currentUserId) {
          console.warn('Current user ID not found');
          return;
        }

        const isUserMessage = message?.senderId === currentUserId;
        const { conversations } = get();
        const currentConv = conversations[conversationId];

        if (!currentConv) {
          return;
        }


        if (isUserMessage && message?.id) {
          const newId = String(message.id);
          // const messageContent = String(message.content ?? '');
          const serverCreatedAt =
            typeof message.createdDate === 'number'
              ? message.createdDate
              : (typeof message.createdAt === 'number' ? message.createdAt : Date.now());

          // Find the *best* matching pending dummy user message.
          // Matching by content alone is risky (e.g., many "hi" messages). We mitigate by choosing the
          // most recent pending message with the closest createdDate to the server timestamp.
          const pendingCandidates = (currentConv.messages ?? [])
            .filter((m) =>
              m.role === MessageRole.USER_CHAT &&
              (m.deliveryStatus !== MessageDeliveryStatus.DELIVERED))
            .map((m) => ({
              recordId: m.recordId,
              createdDate: m.generations?.[0]?.createdDate ?? 0,
            }))
            .sort((a, b) => {
              // Closest to server time first; tie-breaker: latest client createdDate
              const da = Math.abs((a.createdDate ?? 0) - serverCreatedAt);
              const db = Math.abs((b.createdDate ?? 0) - serverCreatedAt);
              if (da !== db) return da - db;
              return (b.createdDate ?? 0) - (a.createdDate ?? 0);
            });

          const foundDummyId = pendingCandidates[0]?.recordId;
          if (!foundDummyId) {
            // No pending dummy to reconcile; if the server echo already exists, _addMessageToActiveConversation
            // will dedupe it anyway.
            return;
          }

          set((state) => {
            const current = state.conversations[conversationId];
            if (!current) return state;

            const messages = current.messages ?? [];

            // If server message already exists, remove the dummy instead of creating a duplicate id.
            const serverAlreadyExists = messages.some((m) => m.role === MessageRole.USER_CHAT && m.recordId === newId);

            const nextMessages = messages
              // Drop any existing server-echo user message if we’re about to convert the dummy into it
              .filter((m) => !(m.role === MessageRole.USER_CHAT && m.recordId === newId && !serverAlreadyExists))
              // If server exists, drop the dummy to avoid duplicates; else keep and convert it below
              .filter((m) => !(serverAlreadyExists && m.role === MessageRole.USER_CHAT && m.recordId === foundDummyId))
              .map((msg) => {
                const isDummyUserMsg = msg.role === MessageRole.USER_CHAT && msg.recordId === foundDummyId;
                const isDummyAssistantLink =
                  msg.role === MessageRole.ASSISTANT && msg.userRecordId === foundDummyId;

                if (isDummyUserMsg && !serverAlreadyExists) {
                  return {
                    ...msg,
                    recordId: newId,
                    deliveryStatus: MessageDeliveryStatus.DELIVERED,
                    generations: (msg.generations ?? []).map((g) =>
                      g.id === foundDummyId ? { ...g, id: newId } : g
                    ),
                  };
                }

                if (isDummyAssistantLink && !serverAlreadyExists) {
                  return {
                    ...msg,
                    userRecordId: newId,
                  };
                }

                return msg;
              });

            return {
              conversations: {
                ...state.conversations,
                [conversationId]: {
                  ...current,
                  messages: nextMessages,
                },
              },
            };
          });

          // Remove from queue using the dummy id (stable), not the content.
          get().removeFromMessageQueue(foundDummyId);
          get().setIsProcessingMessageQueue(false);
          get().processNextMessageFromQueue(conversationId, config);
          return;
        }

        if (!isUserMessage) {
          const userMap = new Map<string, IamData>();
          const allParticipants = currentConv?.participants || [];
          allParticipants.forEach((participant) => {
            userMap.set(participant.itemId, participant);
          });

          // Add removed participants to userMap for lookup
          const removedParticipants = currentConv?.removedParticipants || [];
          removedParticipants.forEach((participant) => {
            // Only add if not already in map (participants take priority)
            if (!userMap.has(participant.itemId)) {
              userMap.set(participant.itemId, participant);
            }
          });

          // Get selected model to add as EagleGpt user
          const selectedModel = useModelStore.getState().selectedModel;

          // Add selected model (EagleGpt) to userMap if it exists
          if (selectedModel) {
            const modelAsUser: IamData = {
              itemId: selectedModel.id,
              createdDate: '',
              lastUpdatedDate: '',
              lastLoggedInTime: '',
              language: '',
              salutation: '',
              firstName: 'EagleGpt',
              lastName: null,
              email: selectedModel.id,
              userName: selectedModel.id,
              phoneNumber: null,
              roles: [],
              permissions: [],
              active: true,
              isVarified: false,
              profileImageUrl: null,
              mfaEnabled: false,
              organizations: [],
              plan: { itemId: '', name: '' },
              planItem: { itemId: '', title: null },
              groups: [],
            };
            userMap.set(selectedModel.id, modelAsUser);
          }

          const convertBackendMentionsToMentionObjects = (
            mentionUserIds: string[],
            content: string
          ): Mention[] => {
            if (!mentionUserIds || mentionUserIds.length === 0 || !content) {
              return [];
            }

            const mentions: Mention[] = [];

            // For each mentioned user ID, find their name in the content
            mentionUserIds.forEach((userId) => {
              const user = userMap.get(userId);
              if (!user) {
                // If user still not found, skip it (we already tried to fetch)
                console.warn('User not found for mention:', userId)
                return;
              }

              const userName = `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`;

              // Find all occurrences of the user name in the content
              let searchStart = 0;
              let index = content.indexOf(userName, searchStart);
              while (index !== -1) {

                // Check if this is a word boundary (not part of another word)
                const beforeChar = index > 0 ? content[index - 1] : ' ';
                const afterChar = index + userName.length < content.length
                  ? content[index + userName.length]
                  : ' ';

                // Only consider it a mention if it's at word boundaries
                if ((beforeChar === ' ' || beforeChar === '\n' || index === 0) &&
                  (afterChar === ' ' || afterChar === '\n' || index + userName.length === content.length)) {
                  mentions.push({
                    userId: userId,
                    trigger: `@${userName}`,
                    start: index,
                    end: index + userName.length,
                    user: user,
                  });
                  // Move past this occurrence
                  searchStart = index + userName.length;
                } else {
                  // Move past this occurrence even if not a match
                  searchStart = index + 1;
                }

                // Find next occurrence
                index = content.indexOf(userName, searchStart);
              }
            });

            // Sort mentions by start position and remove duplicates
            return mentions
              .sort((a, b) => a.start - b.start)
              .filter((mention, index, arr) => {
                // Remove duplicates (same userId and position)
                return index === 0 ||
                  mention.userId !== arr[index - 1].userId ||
                  mention.start !== arr[index - 1].start;
              });
          };
          const messageRecordId = message.id;

          const addAssistantToAttachments = (attachments: Attachment[], sender: MessageRole): FileMetadata[] => {
            return attachments.map((attachment) => {
              return {
                fileId: attachment.fileId,
                fileName: attachment.fileName,
                extension: attachment.extension,
                sender: sender,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                size: 0,
              };
            });
          };
          const mentions = message.mentions && Array.isArray(message.mentions) && message.mentions.length > 0
            ? convertBackendMentionsToMentionObjects(message.mentions, message.content || '')
            : undefined;

          // Helper function to get parentMessage from parentIds
          const getParentMessageFromIds = (parentIds: string[] | undefined): { senderName: string; content: string; time: number } | undefined => {
            if (!parentIds || !Array.isArray(parentIds) || parentIds.length === 0) {
              return undefined;
            }

            const parentId = parentIds[0];
            const parentMsg = currentConv.messages.find(
              (msg) => msg.recordId === parentId ||
                msg.generations.some(gen => gen.id === parentId)
            );

            if (!parentMsg) return undefined;

            // Get sender name from userMap
            let senderName = 'User';
            const parentSenderId = parentMsg.senderId;
            if (parentSenderId) {
              const user = userMap.get(parentSenderId);
              if (user) {
                senderName = `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`;
              } else {
                // Fallback to current user if senderId matches
                const currentUser = useCommonStore.getState().user;
                if (currentUser && currentUser.id === parentSenderId) {
                  senderName = `${currentUser.firstName}${currentUser.lastName ? ` ${currentUser.lastName}` : ''}`;
                }
              }
            }

            // Get content from first generation
            const parentContent = parentMsg.generations[0]?.content || '';
            const parentTime = parentMsg.generations[0]?.createdDate || Date.now();

            return {
              senderName: senderName,
              content: parentContent,
              time: parentTime,
            };
          };

          const parentMessageData = getParentMessageFromIds(message.parentIds);

          const newMessage: Message = {
            recordId: messageRecordId,
            role: MessageRole.ASSISTANT_CHAT,
            senderId: message.senderId,
            generations: [{
              id: messageRecordId,
              content: message.content || '',
              createdDate: new Date(message.createdAt).getTime() || Date.now(),
              attachments: addAssistantToAttachments(message.attachments || [], MessageRole.ASSISTANT),
              ...(message.parentIds && Array.isArray(message.parentIds) && message.parentIds.length > 0 && { parentIds: message.parentIds }),
              ...(parentMessageData && { parentMessage: parentMessageData }),
              ...(mentions && mentions.length > 0 && { mentions: mentions }),
            }],
            
            toolsStatus: message.toolsStatus,
          };




          // console.log('userData', userData, "newPlaceholderMessage", newPlaceholderMessage)




          set((state) => {
            const currentConvState = state.conversations[conversationId];
            if (!currentConvState) {
              return state;
            }

            // Check if this message ID already exists to prevent duplicates
            const messageAlreadyExists = currentConvState.messages.some(
              (msg) =>
                msg.recordId === message.id ||
                (msg.generations && msg.generations.some(gen => gen.id === message.id))
            );

            if (messageAlreadyExists) {
              return state; // Message already processed, skip update
            }
            const updatedMessages = [...currentConvState.messages, newMessage];

            return {
              conversations: {
                ...state.conversations,
                [conversationId]: {
                  ...currentConvState,
                  messages: updatedMessages,

                  isCurrentMessageLoading: false,
                  isCurrentResponseGenerating: true,
                },
              },
            };
          });
        }

      } catch (error) {
        console.error('❌ Error handling group chat notification:', error);
      }
    } else if (type === "ASSISTANT_REQUEST_PROCESSING_STARTED") {
      try {
        if (!notification || !notification.response) return;
        const notificationData = notification.response;
        const assistantPositionData = notification?.streamingResponse?.data;
        const { conversationId, message } = notificationData;
        const currentUser = useCommonStore.getState().user;
        const { conversations } = get();
        const currentConv = conversations[conversationId];

        if (!currentConv || (currentConv.participants?.length ?? 0) < 2) {
          return;
        }
        const assistantMessageId = message.id;
        // userRecordId should be the user message ID that triggered this response
        const userMessageId = message.metadata?.relatedUserMessageIds?.[0] || assistantMessageId;

        const newPlaceholderMessage: Message = {
          recordId: assistantMessageId,
          userRecordId: userMessageId,
          role: MessageRole.ASSISTANT,
          senderId: currentUser?.id,
          generations: [{
            id: assistantMessageId,
            content: '',
            createdDate: Date.now(),
            attachments: [],
            isThisGenerationOnGoing: true,
          }],
        };

        set((state) => {
          const currentConvState = state.conversations[conversationId];
          if (!currentConvState) {
            return state;
          }

          // Check if message already exists to prevent duplicates
          const messageExists = currentConvState.messages.some(
            (msg) => msg.recordId === assistantMessageId || 
                     msg.generations.some(g => g.id === assistantMessageId)
          );

          const updatedActiveStreamsMap = new Map(currentConv.activeStreamsMap);
          updatedActiveStreamsMap.set(userMessageId, true);

          if (messageExists) {
            return {
              conversations:{
                ...state.conversations,
                [conversationId]:{
                  ...currentConvState,
                  assistantResponseRemaining: assistantPositionData?.remaining ?? 0,
                  activeStreamsMap: updatedActiveStreamsMap,
                }
              }
            }
          }

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...currentConvState,
                messages: [...currentConvState.messages, newPlaceholderMessage],
                // Set streamingAssistantMessageId for group chat streaming
                streamingAssistantMessageId: assistantMessageId,
                isCurrentMessageLoading: false,
                isCurrentResponseGenerating: true,
                assistantResponseRemaining: assistantPositionData?.remaining ?? 0,
                activeStreamsMap: updatedActiveStreamsMap,
              },
            },
          };
        });
      } catch (error) {
        console.error('❌ Error handling ASSISTANT_REQUEST_PROCESSING_STARTED notification:', error);
      }
    } else if (type === "ASSISTANT_STREAMED_RESPONSE") {
      const notificationData = notification;
      // const { conversationId, message } = notificationData;
      // console.log('notificationData in assistant streamed response', notificationData)
      const conversationId = notificationData.response.conversationId;
      const message = notificationData.response.message;
      const messageId = message.id;
      const messageSenderId = message.metadata.relatedUserMessageIds[0];
      const parsedResponse = {
        ...notificationData.streamingResponse,
        conversation_id: conversationId,
        assistant_message_id: messageId,
        parentIds: message.parentIds || [],
        messageSenderId: messageSenderId,
      };
   

      // // Process the streaming update using existing handler
      get()._handleWebSocketMessage(parsedResponse, config);
    } else if (type === "ADD_PARTICIPANT") {
      try {
        const { activeConversationId, conversations } = get();
        if (!config || !userIds || !activeConversationId) {
          return;
        }

        const currentConv = conversations[activeConversationId];
        if (!currentConv) {
          return;
        }

        // Fetch user data for sender and invited users
        let participants = currentConv?.participants || [];
        // console.log('participants in add participant', participants)
        let senderData = participants.find(
          participant => participant?.itemId === notification.actorId

        ) || null;
        if (!senderData) {
          const currentUser = useCommonStore.getState().user;
          if (currentUser && currentUser?.id === notification.actorId) {
            senderData = {
              itemId: currentUser.id,
              firstName: currentUser.firstName,
              lastName: currentUser.lastName,
              createdDate: new Date().toISOString(),
              lastUpdatedDate: new Date().toISOString(),
              lastLoggedInTime: new Date().toISOString(),
              language: '',
              salutation: '',
              email: currentUser.email,
              userName: currentUser.email,
              phoneNumber: '',
              roles: [],
              permissions: [],
              active: true,
              isVarified: false,
              profileImageUrl: currentUser.profileImageUrl,
              mfaEnabled: false,
              organizations: [],
              plan: { itemId: '', name: '' },
              planItem: { itemId: '', title: null },
              groups: [],
            } as IamData;
            participants = [...participants, senderData];
          }
        }


        const response = await get().fetchUsersData(notification?.userIdsByType, config);
        const invitedUsersData = response?.data || [];

        if (activeConversationId && invitedUsersData.length > 0) {
          const existingParticipantIds = new Set(participants.map(p => p.itemId));
          const newParticipants = invitedUsersData.filter(p => !existingParticipantIds.has(p.itemId));
          const updatedParticipants = [...participants, ...newParticipants];

          // Get conversationId from notification if available, otherwise use activeConversationId
          const notificationConversationId = notification?.response?.conversationId;
          const isNotActiveConversation = notificationConversationId !== activeConversationId;

          set((state) => ({
            conversations: {
              ...state.conversations,
              [activeConversationId]: {
                ...(state.conversations[activeConversationId] || initialConversationState(activeConversationId)),
                participants: updatedParticipants,
                isGroupChat: true,
                isRecentlyAdded: isNotActiveConversation,
              },
            },
          }));

          // Also update the global participants array and cache
          get().setHasGroupChatParticipantsArray(activeConversationId, updatedParticipants);
        }

        const senderName = senderData?.firstName || 'Someone';
        const invitedUserNames = invitedUsersData
          .map(user => user?.firstName)
          .filter(name => name)
          .join(', ');

        // Check if actorId is in userIdsByType (person joined themselves)
        const isSelfJoin = notification?.actorId && notification?.userIdsByType?.includes(notification.actorId);
        
        const messageContent = isSelfJoin
          ? `${invitedUserNames || senderName || 'Someone'} joined the chat`
          : `${senderName} added ${invitedUserNames}`;
        
        // Add time separator (same pattern as timeline events block around 3521-3581)
        const eventDate = notification?.createdAt ? new Date(notification.createdAt) : new Date();
        const eventTimestamp = eventDate.getTime();

        const timeSeparatorId = uuidv4();
        const timeSeparatorMessage: Message = {
          recordId: timeSeparatorId,
          role: MessageRole.CONVERSATION_TITLE_UPDATED, // Using this role for time separator
          generations: [
            {
              id: timeSeparatorId,
              content: formatDateWithTime(eventDate),
              createdDate: eventTimestamp,
            },
          ],
        };
        get()._addMessageToActiveConversation(timeSeparatorMessage);

        const generationId = uuidv4();

        const systemMessage: Message = {
          recordId: generationId,
          role: MessageRole.ADD_PARTICIPANT,
          generations: [
            {
              id: generationId,
              content: messageContent,
              createdDate: eventTimestamp,
            },
          ],
        };

        // console.log('systemMessage in add participant', systemMessage)
        get()._addMessageToActiveConversation(systemMessage);



      } catch (error) {
        console.error('❌ Error handling ADD_PARTICIPANT notification:', error);
      }
    } else if (type === "USER_ADDED_BY_TOKEN") {
      try {
        const { activeConversationId, conversations } = get();
        if (!config || !userIds || !activeConversationId) {
          return;
        }

        const currentConv = conversations[activeConversationId];
        if (!currentConv) {
          return;
        }

        // Fetch user data for sender and invited users
        let participants = currentConv?.participants || [];
        let senderData = participants.find(
          participant => participant?.itemId === notification.actorId
        ) || null;
        if (!senderData) {
          const currentUser = useCommonStore.getState().user;
          if (currentUser && currentUser?.id === notification.actorId) {
            senderData = {
              itemId: currentUser.id,
              firstName: currentUser.firstName,
              lastName: currentUser.lastName,
              createdDate: new Date().toISOString(),
              lastUpdatedDate: new Date().toISOString(),
              lastLoggedInTime: new Date().toISOString(),
              language: '',
              salutation: '',
              email: currentUser.email,
              userName: currentUser.email,
              phoneNumber: '',
              roles: [],
              permissions: [],
              active: true,
              isVarified: false,
              profileImageUrl: currentUser.profileImageUrl,
              mfaEnabled: false,
              organizations: [],
              plan: { itemId: '', name: '' },
              planItem: { itemId: '', title: null },
              groups: [],
            } as IamData;
            participants = [...participants, senderData];
          }
        }

        const response = await get().fetchUsersData(notification?.userIdsByType, config);
        const invitedUsersData = response?.data || [];

        if (activeConversationId && invitedUsersData.length > 0) {
          const existingParticipantIds = new Set(participants.map(p => p.itemId));
          const newParticipants = invitedUsersData.filter(p => !existingParticipantIds.has(p.itemId));
          const updatedParticipants = [...participants, ...newParticipants];

          // Get conversationId from notification if available, otherwise use activeConversationId
          const notificationConversationId = notification?.response?.conversationId;
          const isNotActiveConversation = notificationConversationId !== activeConversationId;

          set((state) => ({
            conversations: {
              ...state.conversations,
              [activeConversationId]: {
                ...(state.conversations[activeConversationId] || initialConversationState(activeConversationId)),
                participants: updatedParticipants,
                isGroupChat: true,
                isRecentlyAdded: isNotActiveConversation,
              },
            },
          }));

          // Also update the global participants array and cache
          get().setHasGroupChatParticipantsArray(activeConversationId, updatedParticipants);
        }

        const invitedUserNames = invitedUsersData
          .map(user => user?.firstName)
          .filter(name => name)
          .join(', ');

        // Get timestamp from notification or use current time
        const eventDate = notification?.createdAt 
          ? new Date(notification.createdAt) 
          : new Date();
        const eventTimestamp = eventDate.getTime();

        // Show "joined" message - the users in userIdsByType are the ones who joined
        const messageContent = invitedUserNames 
          ? `${invitedUserNames} ${invitedUsersData.length === 1 ? 'joined' : 'joined'} the chat`
          : 'Someone joined the chat4';
        
        const generationId = uuidv4();

        const systemMessage: Message = {
          recordId: generationId,
          role: MessageRole.ADD_PARTICIPANT,
          generations: [
            {
              id: generationId,
              content: messageContent,
              createdDate: eventTimestamp,
            },
          ],
        };

        get()._addMessageToActiveConversation(systemMessage);

      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('❌ Error handling USER_ADDED_BY_TOKEN notification:', error);
      }
    } else if (type === "CONVERSATION_TITLE_UPDATED") {
      // console.log('type in conversation title updated', notification)
      const { activeConversationId, conversations } = get();
      const currentConv = activeConversationId ? conversations[activeConversationId] : null;
      const participants = currentConv?.participants || [];
      // console.log('participants', participants, "notification.actorId", notification)
      const updatedBy = participants.find((participant: any) => participant.itemId === notification.actorId);
      const messageContent = `${updatedBy?.firstName || 'Someone'} updated the conversation title`;
      const generationId = uuidv4();
      const systemMessage: Message = {
        recordId: generationId,
        role: MessageRole.CONVERSATION_TITLE_UPDATED,
        generations: [
          {
            id: generationId,
            content: messageContent,
            createdDate: Date.now(),
          },
        ],
      };
      // console.log('systemMessage in conversation title updated', systemMessage)
      get()._addMessageToActiveConversation(systemMessage);
    } else if (type === "REMOVE_PARTICIPANT") {
      // Get current participants and removed user IDs
      const { activeConversationId, conversations } = get();
      const currentConv = activeConversationId ? conversations[activeConversationId] : null;
      const currentParticipants = currentConv?.participants || [];
      const removedUserIds = notification.userIdsByType || [];

      if (!removedUserIds.length) {
        return;
      }

      const remover = currentParticipants.find(
        (participant: any) => participant.itemId === notification.actorId
      );

      // Check if the actor is removing themselves
      const isSelfRemoval = removedUserIds.includes(notification.actorId);

      // Get the participants being removed
      const removedParticipants = currentParticipants.filter(
        (participant: any) => removedUserIds.includes(participant.itemId)
      );

      // Determine message content based on whether it's self-removal
      let messageContent: string;
      if (isSelfRemoval) {
        // If actor removed themselves, show "left the conversation"
        const leavingUser = removedParticipants.find(
          (participant: any) => participant.itemId === notification.actorId
        ) || remover;
        const leavingUserName = leavingUser?.firstName || 'Someone';
        messageContent = `${leavingUserName} left the conversation`;
      } else {
        // Otherwise, show "removed {users}"
        const removedParticipantNames = removedParticipants
          .map((participant: any) => participant.firstName)
          .filter((name: string) => name)
          .join(', ') || 'users';
        messageContent = `${remover?.firstName || 'Someone'} removed ${removedParticipantNames}`;
      }

      // Update participants list by excluding removed users
      const remainingParticipants = currentParticipants.filter(
        (participant: any) => !removedUserIds.includes(participant.itemId)
      );

      // Get existing removed participants and merge with new ones
      const existingRemovedParticipants = currentConv?.removedParticipants || [];
      const updatedRemovedParticipants = [
        ...existingRemovedParticipants,
        ...removedParticipants.filter(
          (removed) => !existingRemovedParticipants.some((existing) => existing.itemId === removed.itemId)
        )
      ];

      // Update participants in conversation state and global state
      if (activeConversationId) {
        set((state) => ({
          conversations: {
            ...state.conversations,
            [activeConversationId]: {
              ...(state.conversations[activeConversationId] || initialConversationState(activeConversationId)),
              participants: remainingParticipants,
              removedParticipants: updatedRemovedParticipants,
            },
          },
        }));
        // Also update the cache
        get().setHasGroupChatParticipantsArray(activeConversationId, remainingParticipants);
      }
      get().setHasParticipantsArray(remainingParticipants);

      // Create and add system message
      const generationId = uuidv4();
      const systemMessage: Message = {
        generations: [
          {
            id: generationId,
            content: messageContent,
            createdDate: Date.now(),
          },
        ],
        recordId: generationId,
        role: MessageRole.USER_REMOVED,
      };

      get()._addMessageToActiveConversation(systemMessage);
    }else if (type === "CONVERSATION_CREATED") {
      // console.log('type in conversation title updated', notification)
      const { activeConversationId, conversations } = get();
      const currentConv = activeConversationId ? conversations[activeConversationId] : null;
      const participants = currentConv?.participants || [];
      // console.log('participants', participants, "notification.actorId", notification)
      const createdBy = participants.find((participant: any) => participant.itemId === notification.actorId) || useCommonStore.getState().user?.id === notification.actorId ? useCommonStore.getState().user : null;
 
      const messageContent = `${createdBy?.firstName} created the conversation`;
      const generationId = uuidv4();
 
      // console.log('messageContent in conversation created', messageContent,"participants", participants)
      const systemMessage: Message = {
        recordId: generationId,
        role: MessageRole.CONVERSATION_CREATED,
        generations: [
          {
            id: generationId,
            content: messageContent,
            createdDate: Date.now(),
          },
        ],
      };
      // console.log('systemMessage in conversation created', systemMessage)
      get()._addMessageToActiveConversation(systemMessage);
    }


  },
  /**
   * Batch process streaming messages for optimal performance.
   * This function processes multiple ASSISTANT_STREAMED_RESPONSE messages
   * and accumulates content updates before triggering a single state update.
   */
  handleStreamingMessageBatch: (messages: any[], config: ConfigurationSettings) => {
    if (!messages || messages.length === 0 || !config) return;

    // Process all messages - don't skip any to ensure all content is captured
    for (const notification of messages) {
      if (!notification?.response?.conversationId || !notification?.response?.message?.id) {
        continue;
      }

      const conversationId = notification.response.conversationId;
      // Re-fetch conversations for each message to get the latest state
      // This ensures we see placeholder messages created by prior processing
      const { conversations } = get();
      const currentConv = conversations[conversationId];

      // If conversation doesn't exist yet, skip this message
      if (!currentConv) {
        console.warn('Conversation not found for streaming message:', conversationId);
        continue;
      }

      const message = notification.response.message;
      const messageId = message.id;
      const messageSenderId = message.metadata?.relatedUserMessageIds?.[0];
      
      // Check if we have a message with this ID before processing
      const messageExists = currentConv.messages.some(
        (msg) => msg.recordId === messageId || 
                 msg.generations.some(g => g.id === messageId)
      );
      
      if (!messageExists && currentConv.isGroupChat) {
        // Message placeholder not created yet - create it now
        // This can happen when ASSISTANT_REQUEST_PROCESSING_STARTED arrives in a different batch
        const currentUser = useCommonStore.getState().user;
        const userMessageId = messageSenderId || messageId;

        const newPlaceholderMessage: Message = {
          recordId: messageId,
          userRecordId: userMessageId,
          role: MessageRole.ASSISTANT,
          senderId: currentUser?.id,
          generations: [{
            id: messageId,
            content: '',
            createdDate: Date.now(),
            attachments: [],
          }],
        };

        // Create placeholder synchronously before processing streaming content
        set((state) => {
          const conv = state.conversations[conversationId];
          if (!conv) return state;

          // Double-check it wasn't created between our check and now
          const alreadyExists = conv.messages.some(
            (msg) => msg.recordId === messageId || 
                     msg.generations.some(g => g.id === messageId)
          );

          if (alreadyExists) return state;

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                messages: [...conv.messages, newPlaceholderMessage],
                streamingAssistantMessageId: messageId,
                isCurrentMessageLoading: false,
                isCurrentResponseGenerating: true,
              },
            },
          };
        });
      }

      const parsedResponse = {
        ...notification.streamingResponse,
        conversation_id: conversationId,
        assistant_message_id: messageId,
        parentIds: message.parentIds || [],
        messageSenderId: messageSenderId,
      };

      // Process each message using existing handler
      get()._handleWebSocketMessage(parsedResponse, config);
    }
  },

  handleStreamingQueue: (streamingQueue: StreamingQueueItem[], config: ConfigurationSettings) => {
    const collaborationStore = useCollaborationSocketStore.getState();

    if (!streamingQueue || streamingQueue.length === 0) {
      return;
    }

    // Group queue items by messageID to avoid creating duplicate messages
    const messagesMap = new Map<string, {
      conversationId: string;
      messageID: string;
      userID: string[] | null;
      parentIds: string[];
      streamingResponses: any[];
      type: string;
      positions: number[];
    }>();

    streamingQueue.forEach((queueItem) => {
      const { conversationId, messageID, userID, parentIds, streamingResponse, position } = queueItem;

      // console.log('queueItem', queueItem)

      if (!conversationId || !messageID || !streamingResponse || streamingResponse.length === 0) {
        return;
      }

      const key = `${conversationId}-${messageID}`;
      if (messagesMap.has(key)) {
        // Append streaming responses to existing entry
        const existing = messagesMap.get(key)!;
        existing.streamingResponses.push(...streamingResponse);
        existing.positions.push(position);
      } else {
        // Create new entry
        messagesMap.set(key, {
          conversationId,
          messageID,
          userID,
          parentIds,
          streamingResponses: [...streamingResponse],
          type: queueItem.type,
          positions: [position],
        });
      }
    });
    // console.log('messagesMap', messagesMap)

    // Track positions of processed messages (only remove from queue if processed)
    const processedPositions: number[] = [];

    // Process each unique messageID only once
    messagesMap.forEach((messageData) => {
      const { conversationId, messageID, userID, parentIds, streamingResponses, positions } = messageData;
      const userMessageId = userID && Array.isArray(userID) && userID[0] ? userID[0] : undefined;
      const serverAssignedMessageId = messageID;

      // Check if user message is delivered before processing streaming responses
      const currentState = get();
      const conv = currentState.conversations[conversationId];

      if (!conv || !conv.messages || !Array.isArray(conv.messages)) {
        return;
      }

      // For group chats, check if the user message is delivered
      const hasParticipants = conv?.participants && conv.participants.length > 0;
      if (hasParticipants && userMessageId) {
        const userMessage = conv.messages.find(
          (msg) => msg.recordId === userMessageId &&
            (msg.role === MessageRole.USER_CHAT || msg.role === MessageRole.ASSISTANT_CHAT)
        );

        console.log('userMessage', userMessage)
        // If user message doesn't exist yet, skip processing (HTTP response hasn't completed)
        if (!userMessage) {
          // Message doesn't exist yet, skip processing but keep in queue
          return;
        }
        // Only process streaming if message is delivered or doesn't have delivery status (for backward compatibility)
        if (userMessage.deliveryStatus && userMessage.deliveryStatus !== MessageDeliveryStatus.DELIVERED) {
          // Message not delivered yet, skip processing but keep in queue
          return;
        }
      }

      processedPositions.push(...positions);

      // First, create/update the message structure in chat store (similar to original logic)
      set((state) => {
        const conv = state.conversations[conversationId];
        // console.log('conv in handleStreamingQueue', conv)
        if (!conv || !conv.messages || !Array.isArray(conv.messages)) return state;

        const hasParticipants = conv?.participants && conv.participants.length > 0;

        // This is how placeholders are set in sendGroupChatStream
        let messageIndex = -1;
        if (userMessageId) {
          messageIndex = conv.messages.findIndex(
            (msg) => msg.role === MessageRole.ASSISTANT &&
              msg.userRecordId === userMessageId
          );
        }

        // If not found, find existing message by serverAssignedMessageId
        if (messageIndex === -1) {
          messageIndex = conv.messages.findIndex(
            (msg) => msg.role === MessageRole.ASSISTANT &&
              msg.generations.some(gen => gen.id === serverAssignedMessageId)
          );
        }

        // console.log('messageIndex in handleStreamingQueue', messageIndex)
        // If not found, try matching by streamingAssistantMessageId (for normal chat)
        if (messageIndex === -1 && !hasParticipants && conv.streamingAssistantMessageId) {
          messageIndex = conv.messages.findIndex(
            (msg) => msg.role === MessageRole.ASSISTANT &&
              msg.generations.some(gen => gen.id === conv.streamingAssistantMessageId)
          );
        }

        // If still not found, check for placeholder by userRecordId (only for group chat)
        if (messageIndex === -1 && userMessageId && hasParticipants) {
          messageIndex = conv.messages.findIndex(
            (msg) => msg.role === MessageRole.ASSISTANT &&
              msg.userRecordId === userMessageId
          );
          // console.log('messageIndex in handleStreamingQueue', messageIndex)
        }

        // Handle regeneration case - add generation to parent message
        if (messageIndex === -1 && parentIds && Array.isArray(parentIds) && parentIds.length > 0) {
          for (const parentId of parentIds) {
            const parentMsgIndex = conv.messages.findIndex(
              (msg) => msg.recordId === parentId &&
                (msg.role === MessageRole.ASSISTANT || msg.role === MessageRole.ASSISTANT_CHAT)
            );

            if (parentMsgIndex !== -1) {
              const parentMsg = conv.messages[parentMsgIndex];
              const generationExists = parentMsg.generations.some(gen => gen.id === serverAssignedMessageId);

              if (!generationExists) {
                // Add new generation to parent message
                // const userMessageData = getUserMessageData(conv, userMessageId);
                const updatedMessages = conv.messages.map((msg, index) => {
                  if (index === parentMsgIndex) {
                    const newGenerations = [
                      ...msg.generations,
                      {
                        id: serverAssignedMessageId,
                        content: '',
                        createdDate: Date.now(),
                        parentIds: parentIds,
                      }
                    ];
                    return {
                      ...msg,
                      // ...(userMessageData && !msg.userMessage && { userMessage: userMessageData }),
                      generations: newGenerations,
                      activeGenerationIndex: newGenerations.length - 1,
                    };
                  }
                  return msg;
                });

                return {
                  conversations: {
                    ...state.conversations,
                    [conversationId]: {
                      ...conv,
                      messages: updatedMessages,
                    },
                  },
                };
              } else {
                // Generation exists, set activeGenerationIndex
                const generationIndex = parentMsg.generations.findIndex(gen => gen.id === serverAssignedMessageId);
                if (generationIndex !== -1) {
                  const updatedMessages = conv.messages.map((msg, index) => {
                    if (index === parentMsgIndex) {
                      return {
                        ...msg,
                        activeGenerationIndex: generationIndex,
                      };
                    }
                    return msg;
                  });

                  return {
                    conversations: {
                      ...state.conversations,
                      [conversationId]: {
                        ...conv,
                        messages: updatedMessages,
                      },
                    },
                  };
                }
              }
              // Found parent, break and continue to process streaming update
              break;
            }
          }
        }

        console.log('messageIndex', messageIndex)
        if (messageIndex !== -1) {
          const existingMsg = conv.messages[messageIndex];
          const isPlaceholder = existingMsg.userRecordId === userMessageId;

          const updatedMessages = conv.messages
            .filter((msg, index) => {
              if (index === messageIndex) return true;
              if (msg.role === MessageRole.ASSISTANT) {
                const hasMatchingGeneration = msg.generations.some(gen => gen.id === serverAssignedMessageId);
                const isDuplicatePlaceholder = (msg.userRecordId === userMessageId) &&
                  index !== messageIndex;
                return !hasMatchingGeneration && !isDuplicatePlaceholder;
              }
              return true;
            })
            .map((msg, index) => {
              const actualIndex = index === messageIndex ? messageIndex :
                conv.messages.findIndex(m => m === msg);

              if (actualIndex === messageIndex) {
                // Update the target message
                const updatedGenerations = msg.generations.map(gen => {
                  // If it's a placeholder, update ID to serverAssignedMessageId and clear typing content
                  if (isPlaceholder) {
                    const hasTypingContent = gen.content === 'EagleGpt is typing...';
                    return {
                      ...gen,
                      id: serverAssignedMessageId,
                      ...(hasTypingContent && { content: '' }),
                    };
                  }

                  // Ensure generation ID matches server ID
                  if (gen.id === serverAssignedMessageId) {
                    return gen;
                  }

                  // Update if it's a streaming ID
                  if (!hasParticipants && gen.id === conv.streamingAssistantMessageId) {
                    return {
                      ...gen,
                      id: serverAssignedMessageId,
                    };
                  }
                  return gen;
                });

                return {
                  ...msg,
                  ...(isPlaceholder && {
                    recordId: serverAssignedMessageId,

                  }),
                  generations: updatedGenerations,
                };
              }
              return msg;
            });

          // For normal chat: Update streamingAssistantMessageId to serverAssignedMessageId
          // For group chat: Don't update to prevent breaking concurrent streams
          const shouldUpdateStreamingId = !hasParticipants && serverAssignedMessageId &&
            (serverAssignedMessageId !== conv.streamingAssistantMessageId);

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                messages: updatedMessages,
                ...(shouldUpdateStreamingId && { streamingAssistantMessageId: serverAssignedMessageId }),
              },
            },
          };
        }
        // console.log('messageIndex', messageIndex)

        // Message doesn't exist - create new one
        const placeholderIndex = conv.messages.findIndex(
          (msg) => msg.role === MessageRole.ASSISTANT && (
            msg.recordId === `${serverAssignedMessageId}-placeholder`
          )
        );

        // console.log('placeholderIndex', placeholderIndex)
        // console.log('conv.messages', conv.messages)

        // const userMessageData = getUserMessageData(conv, userMessageId);

        if (placeholderIndex !== -1) {
          // Replace placeholder
          const updatedMessages = conv.messages.map((msg, index) => {
            if (index === placeholderIndex) {
              const placeholderGen = msg.generations[0];
              return {
                ...msg,
                recordId: serverAssignedMessageId,
                ...(userMessageId && { userRecordId: userMessageId }),
                // ...(userMessageData && { userMessage: userMessageData }),
                generations: [{
                  id: serverAssignedMessageId,
                  content: '',
                  createdDate: placeholderGen?.createdDate || Date.now(),
                  attachments: [],
                }],
              };
            }
            return msg;
          });

          const shouldSetStreamingId = (!hasParticipants && serverAssignedMessageId) ||
            (!conv.streamingAssistantMessageId && serverAssignedMessageId);

          return {
            conversations: {
              ...state.conversations,
              [conversationId]: {
                ...conv,
                messages: updatedMessages,
                ...(shouldSetStreamingId && { streamingAssistantMessageId: serverAssignedMessageId }),
              },
            },
          };
        }

        // Create new message - but double-check it doesn't exist (race condition protection)
        const finalCheck = conv.messages.find(
          (msg) => msg.role === MessageRole.ASSISTANT && (
            msg.generations.some(gen => gen.id === serverAssignedMessageId) ||
            msg.recordId === serverAssignedMessageId
          )
        );

        if (finalCheck) {
          // Message was created by concurrent operation, just return current state
          return state;
        }

        const newAssistantMessage: Message = {
          recordId: serverAssignedMessageId,
          ...(userMessageId && { userRecordId: userMessageId }),
          // ...(userMessageData && { userMessage: userMessageData }),
          role: MessageRole.ASSISTANT,
          senderId: 'auto',
          generations: [{
            id: serverAssignedMessageId,
            content: '',
            createdDate: Date.now(),
            attachments: [],
          }],
        };

        const shouldSetStreamingId = (!hasParticipants && serverAssignedMessageId) ||
          (!conv.streamingAssistantMessageId && serverAssignedMessageId);

        return {
          conversations: {
            ...state.conversations,
            [conversationId]: {
              ...conv,
              messages: [...conv.messages, newAssistantMessage],
              ...(shouldSetStreamingId && { streamingAssistantMessageId: serverAssignedMessageId }),
            },
          },
        };


      });

      // console.log('streamingResponses', streamingResponses)

      // Now process each streamingResponse to update the content
      streamingResponses.forEach((response: any) => {
        if (!response) return;

        // Create parsedResponse similar to ASSISTANT_STREAMED_RESPONSE handling
        const parsedResponse = {
          ...response,
          conversation_id: conversationId,
          assistant_message_id: messageID,
          parentIds: parentIds || [],
        };

        // Process the streaming update using existing handler
        get()._handleWebSocketMessage(parsedResponse, config);
      });
    });

    // Remove only processed items from the queue (by position)
    // Only positions for messages that passed delivery check are included
    if (processedPositions.length > 0) {
      const removeFromStreamingQueue = (collaborationStore as any).removeFromStreamingQueue;
      if (removeFromStreamingQueue) {
        removeFromStreamingQueue(processedPositions);
      }
    }
  },
  _sortSidebarConversations: (list: SidebarItem[]): SidebarItem[] => {
    return [...list].sort(
      (a, b) => new Date(b.LastUpdatedDate).getTime() - new Date(a.LastUpdatedDate).getTime()
    );
  },

  fetchSidebarConversations: async (
    config: ConfigurationSettings,
    params: fetchConversationsListParams = {
      page: 0,
      pageSize: 20,
      isGroupConversation: false,
    }
  ) => {

    set({ isSidebarLoading: true, sidebarError: null });
    try {

      const payload = {
        "page": (params?.page ?? 0) + 1,
        "pageSize": params?.pageSize ?? 50,
        "isArchived": false,
        "projectId": "",
        "isGroupConversation": params.isGroupConversation ?? false
      };
      const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
      const url = `${baseUrl}${config.paths.taskforce.collaborationConversation}`;
      const res = await clients.post<{ data: ApiConversationResponse[], totalCount: number }>(
        `${url}`,
        config.blocksKey,
        JSON.stringify(payload)
      );

      if (res && res.data) {
        const mappedData: SidebarItem[] = res.data.map((item) => ({
          ConversationId: item.conversationId,
          ConversationTitle: item.conversationTitle,
          CreatedBy: item.createdBy,
          TenantId: item.tenantId,
          CreatedDate: item.createdAt,
          LastUpdatedDate: item.lastUpdatedAt,
          Conversations: item.messages,
          ProjectId: item.projectId ?? undefined,
          IsGroup: params.isGroupConversation ?? false,
          ShareToken: item.shareMetadata?.[0]?.token ?? undefined,
          CustomGptId: item.customGptId ?? undefined,
        }));
    
      // Initialize shareTokens map from fetched data
      const shareTokens: Record<string, string> = {};
      mappedData.forEach((item) => {
        if (item.ShareToken) {
          shareTokens[item.ConversationId] = item.ShareToken;
        }
      });
        set((state) => {
          const isGroup = params.isGroupConversation ?? false;
          const isFirstPage = (params?.page ?? 0) === 0;

          let newList: SidebarItem[];
          if (isFirstPage) {
            // First page: replace existing items of this type
            const otherTypeConversations = state.sidebarConversationsList.filter(
              (item) => item.IsGroup !== isGroup
            );
            newList = [...otherTypeConversations, ...mappedData];
          } else {
            // Subsequent pages: append and ensure uniqueness
            const existingIds = new Set(state.sidebarConversationsList.map(item => item.ConversationId));
            const uniqueNewItems = mappedData.filter(item => !existingIds.has(item.ConversationId));
            newList = [...state.sidebarConversationsList, ...uniqueNewItems];
          }

          const sortedResults = get()._sortSidebarConversations(newList);

          return {

            sidebarConversationsList: sortedResults,
            searchConversationsList: sortedResults,
            totalIndividualConversationsCount: isGroup
              ? state.totalIndividualConversationsCount
              : res.totalCount,
            totalGroupConversationsCount: isGroup
              ? res.totalCount
              : state.totalGroupConversationsCount,
            totalConversationsCount: res.totalCount,
            isSidebarLoading: false,
        shareTokens: { ...get().shareTokens, ...shareTokens },

          };
        });
      } else {
        throw new Error('Invalid response structure from conversations-list API');
      }
    } catch (error) {
      console.error('Failed to fetch sidebar conversations:', error);
      set({
        isSidebarLoading: false,
        sidebarError: error instanceof Error ? error.message : 'Unknown error fetching sidebar',
      });
    }
  },

  removeConversationFromSidebarById: (conversationId: string) => {
    if (!conversationId) return;
    set((state) => ({
      sidebarConversationsList: state.sidebarConversationsList.filter(
        (item) => item.ConversationId !== conversationId
      ),
    }));
  },

  removeConversationFromSidebarProjectListById: (conversationId: string) => {
    set((state) => ({
      sidebarProjectsList: state.sidebarProjectsList.map((item) => ({
        ...item,
        items: item.items?.filter((conv) => conv.ConversationId !== conversationId),
      })),
    }));
  },

  appendToSidebarConversationsList: (newItem: SidebarItem) => {
    set((state) => {
      // Prevent duplicates if the item somehow already exists
      if (
        state.sidebarConversationsList.find((it) => it.ConversationId === newItem.ConversationId)
      ) {
        // If it exists, we might want to call _moveConversationToToday instead,
        // but for a true "append new", it shouldn't exist.
        // console.warn(`appendToSidebarConversationsList: Item ${newItem.ConversationId} already exists. Moving to top.`);
        // For now, let's ensure it's at the top if it exists, or add it if it doesn't.
        // This might be redundant if _moveConversationToToday is called.
        // Let's simplify: if called, assume it's new and should be at the top.
        const filteredList = state.sidebarConversationsList.filter(
          (it) => it.ConversationId !== newItem.ConversationId
        );
        return { sidebarConversationsList: [newItem, ...filteredList] }; // newItem is newest
      }
      // newItem is the newest, so prepend it. The list remains sorted.
      return { sidebarConversationsList: [newItem, ...state.sidebarConversationsList] };
    });
  },

  appendProjectItemToSidebarProjectsList: (newItem: ProjectItem, projectId: string) => {
    set((state) => {
      const project = state.sidebarProjectsList.find((it) => it.id === projectId);
      if (project) {
        const updatedItems = [...project.items, newItem];
        updatedItems.sort((a, b) => new Date(b.LastUpdatedDate).getTime() - new Date(a.LastUpdatedDate).getTime());
        return {
          sidebarProjectsList: state.sidebarProjectsList.map((project) => {
            if (project.id === projectId) {
              return { ...project, items: updatedItems };
            }
            return project;
          })
        };
      }
      return { sidebarProjectsList: [...state.sidebarProjectsList] };
    });
  },

  appendProjectToSidebarProjectsList: (newItem: Project) => {
    set((state) => {
      return { sidebarProjectsList: [...state.sidebarProjectsList, newItem] };
    });
  },

  _moveConversationToToday: (conversationId: string) => {
    set((state) => {
      const itemIndex = state.sidebarConversationsList.findIndex(
        (it) => it.ConversationId === conversationId
      );

      if (itemIndex === -1) {
        // console.warn(`_moveConversationToToday: Item ${conversationId} not found in sidebar. It might be a new chat handled by append.`);
        // If item is not in sidebar, it might be a brand new chat not yet fetched,
        // or an edge case. sendMessage should handle adding new items via appendToSidebarConversationsList.
        return {};
      }

      const itemToMove = { ...state.sidebarConversationsList[itemIndex] };
      itemToMove.LastUpdatedDate = new Date().toISOString();

      // Update title from main conversation state if available and more specific
      const conversationState = state.conversations[conversationId];
      console.log('conversationState', conversationState)
      // if (
      //   conversationState &&
      //   conversationState.title &&
      //   conversationState.title !== 'New Chat' &&
      //   conversationState.title !== 'Loading...'
      // ) {
      //   itemToMove.ConversationTitle = conversationState.title;
      // }

      const newListWithoutItem = [
        ...state.sidebarConversationsList.slice(0, itemIndex),
        ...state.sidebarConversationsList.slice(itemIndex + 1),
      ];

      // Add the updated item to the beginning, making it the most recent.
      return { sidebarConversationsList: [itemToMove, ...newListWithoutItem] };
    });
  },

  fetchSearchResults: async (search: string, config: ConfigurationSettings) => {
    try {

      const payload = {
        "page": 1,
        "pageSize": 100,
        "isArchived": false,
        "searchQuery": search,
      }

      const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
      const url = `${baseUrl}${config.paths.taskforce.collaborationConversation}`;
      const res = await clients.post<{ data: ApiConversationResponse[], totalCount: number }>(
        `${url}`,
        config.blocksKey,
        JSON.stringify(payload)
      );

      if (res && res.data) {
        const mappedData: SidebarItem[] = res.data.map((item) => ({
          ConversationId: item.conversationId,
          ConversationTitle: item.conversationTitle,
          CreatedBy: item.createdBy,
          TenantId: item.tenantId,
          CreatedDate: item.createdAt,
          LastUpdatedDate: item.lastUpdatedAt,
          Conversations: item.messages,
          ProjectId: item.projectId ?? undefined,
        }));

        const sortedResults = get()._sortSidebarConversations(mappedData);
        set({
          searchConversationsList: sortedResults,
        });
      } else {
        throw new Error('Invalid response structure from conversations-list API');
      }
    } catch (error) {
      console.error('Failed to fetch search results:', error);
    }
  },

  changeActiveGenerationIndex: (recordId: string, index: number) => {
    set((state) => {
      const activeConversationId = state.activeConversationId;
      if (!activeConversationId) return {};
      const conversation = state.conversations[activeConversationId];
      if (!conversation) return {};
      const message = conversation.messages.find(
        (m) => (m.role === 'assistant' || m.role === 'user') && m.recordId === recordId
      );
      if (!message) return {};
      return {
        conversations: {
          ...state.conversations,
          [activeConversationId]: {
            ...conversation,
            messages: conversation.messages.map((m) => {
              if ((m.role === 'assistant' || m.role === 'user') && m.recordId === recordId) {
                return {
                  ...m,
                  activeGenerationIndex: index,
                };
              }
              return m;
            }),
          },
        },
      };
    });
  },

  updateMessageFeedback: (recordId: string, messageId: string, feedback: boolean | undefined) => {
    set((state) => {
      const conversationId = state.activeConversationId;
      if (!conversationId) return {};
      const conversation = state.conversations[conversationId];
      if (!conversation) return {};

      let didUpdate = false;
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conversation,
            messages: conversation.messages.map((m) => {
              if (m.role !== 'assistant') return m;

              const matchesRecordId =
                !!recordId && (m.recordId === recordId || m.userRecordId === recordId);
              const hasGeneration = (m.generations ?? []).some((g) => g.id === messageId);

              if (!matchesRecordId && !hasGeneration) return m;

              const updatedGenerations = (m.generations ?? []).map((g) => {
                if (g.id !== messageId) return g;
                didUpdate = true;
                return {
                  ...g,
                  feedback,
                };
              });
              if (!didUpdate) return m;

              return {
                ...m,
                generations: updatedGenerations,
              };
            }),
          },
        },
      };
    });
  },
  setNewChat: (isNew: boolean) => {
    set({
      isNewChat: isNew,
      disableNewChat: false,
      hasParticipant: false,
      hasParticipantsArray: [],
      hasAddedAssistantAsParticipant: false,
      headerTitle: DEFAULT_CONVERSATION_NAME,
      activeConversationId: null,
      isLoadingParticipants: false,
    });
    // useModelStore.getState().setSelectedModel('auto');
  },
  disableNewChat: false,
  setDisableNewChat: (disable: boolean) => {
    set({ disableNewChat: disable });
  },
  setNewChatWithModel: (isNew: boolean) => {
    console.log('isNew in setNewChatWithModel', isNew)
    set({
      // isNewChat: isNew,
      hasParticipant: false,
      hasParticipantsArray: [],
      hasAddedAssistantAsParticipant: false,
      headerTitle: DEFAULT_CONVERSATION_NAME,
      activeConversationId: null,
      isLoadingParticipants: false,
    });

  },
  setHasParticipant: (value: boolean) => {
    set({ hasParticipant: value });
  },
  setHasParticipantsArray: (value: IamData[]) => {
    set((state) => {
      const activeConversationId = state.activeConversationId;
      return {
        hasParticipantsArray: value,
        ...(activeConversationId && {
          conversations: {
            ...state.conversations,
            [activeConversationId]: {
              ...(state.conversations[activeConversationId] || initialConversationState(activeConversationId)),
              participants: value,
            },
          },
        }),
      };
    });
  },
  setIsLoadingParticipants: (value: boolean) => {
    set({ isLoadingParticipants: value });
  },
  reset: () => {
    set({
      activeConversationId: null,
      activeAudioMessageId: null,
      conversations: {},
      sidebarConversationsList: [],
      searchConversationsList: [],
      sidebarProjectsList: [],
      isSidebarLoading: false,
      sidebarError: null,
      isNewChat: false,
      hasParticipant: false,
      hasParticipantsArray: [],
      isLoadingParticipants: false,
      isGroupChat: false,
      itemIdFilePathMap: new Map<string, string>(),
    });
  },

  resetErrorByConversationId: (conversationId: string) => {
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          error: null,
        },
      },
    }));
  },

  getSearchRelatedList: async ({ conversationId, messageId, type, modelName }, config: ConfigurationSettings) => {

    try {
      const response = await getSearchRelatedQuestionList({ conversationId, messageId, type, modelName }, config);
      get().setrelatedSearcheQuestionsList(conversationId, response as RelativeSearchQuestion[], messageId);
      return response;
    } catch (error) {
      get().setrelatedSearcheQuestionsList(conversationId, [] as RelativeSearchQuestion[], messageId);
    }
  },

  getSearchRelatedImageList: async ({ conversationId, messageId, type, modelName }, config: ConfigurationSettings) => {
    try {
      const response = await getSearchRelatedQuestionList({ conversationId, messageId, type, modelName }, config);
      get().setrelatedSearcheImageList(conversationId, response as RelativeSearchQuestion[], messageId);
      return response;
    } catch (error) {
      get().setrelatedSearcheImageList(conversationId, [] as RelativeSearchQuestion[], messageId);
      console.log(error);
    }
  },

  getSearchRelatedVideoList: async ({ conversationId, messageId, type, modelName }, config: ConfigurationSettings) => {
    try {
      const response = await getSearchRelatedQuestionList({ conversationId, messageId, type, modelName }, config);
      get().setrelatedSearcheVideoList(conversationId, response as RelativeSearchQuestion[], messageId);
      return response;
    } catch (error) {
      get().setrelatedSearcheVideoList(conversationId, [] as RelativeSearchQuestion[], messageId);
      console.log(error);
    }
  },

  fetchUsersData: async (userIds: string[], config: ConfigurationSettings) => {
    const response = await getUsers({
      page: 0,
      pageSize: 100,
      filter: {
        userIds: userIds,
      },
    }, config);
    return response;
  },

  updateMessageAnalytics: (recordId: string, messageId: string, analytics: ResponseTimeAnalytics | undefined) => {
    set((state) => {
      const conversationId = state.activeConversationId;
      if (!conversationId) return {};
      const conversation = state.conversations[conversationId];
      if (!conversation) return {};
      const message = conversation.messages.find(
        (m) => m.role === 'assistant' && m.userRecordId === recordId
      );
      if (!message) return {};
      return {
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...conversation,
            messages: conversation.messages.map((m) => {
              if (m.role === 'assistant' && m.userRecordId === recordId) {
                m.generations = m.generations.map((g) => {
                  if (g.id === messageId) {
                    g.responseAnalytics = analytics;
                  }
                  return g;
                });
              }
              return m;
            }),
          },
        },
      };
    });
  },

  stopGeneration: async (conversationId: string, config: ConfigurationSettings) => {
    const streamingAssistantMessageId = get().conversations[conversationId].streamingAssistantMessageId;
    if (!streamingAssistantMessageId) return;

    let error: string | null = null;

    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...state.conversations[conversationId],
          isCancelOnGoing: true
        },
      }
    }))
    
    try{
      await stopGeneration(conversationId, streamingAssistantMessageId, config);
    }catch(err){
      error = "COUDNT_STOP_GENERATION"
    }

    if(error){
      set((state) => ({
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...state.conversations[conversationId],
            error
          },
        }
      }))
    }
  }
}));






